import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Car, ChevronDown, ChevronUp, Users, UserPlus, Calendar, Phone, User, X, Pencil, Copy, Plus, Settings, LogIn, LogOut, HelpCircle, Radio, Printer, Check, History, Clock, Ban, AlertTriangle, Mic, MicOff, UserX } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { toast } from "sonner";
import clienteApi from "@/lib/clienteApi";
import { getOperadorNome } from "@/lib/operador";
import { getMonitStatus } from "@/lib/monitoramento";
import VisitanteObsSaidaModal from "@/components/VisitanteObsSaidaModal";
import RegistrarAcessoFixoModal from "@/components/RegistrarAcessoFixoModal";
import HistoricoMovimentosVisitanteModal from "@/components/cliente/HistoricoMovimentosVisitanteModal";
import FinalizarCicloModal from "@/components/cliente/FinalizarCicloModal";
import AgendasHojeModal from "@/components/AgendasHojeModal";

/**
 * Validador clássico de CPF brasileiro (módulo 11) — aceita string com pontuação.
 * Retorna true se o CPF for válido.
 */
const isCpfValido = (cpfRaw) => {
  const cpf = (cpfRaw || "").replace(/\D/g, "");
  if (cpf.length !== 11) return false;
  if (/^(\d)\1+$/.test(cpf)) return false; // todos iguais
  let soma = 0;
  for (let i = 0; i < 9; i++) soma += parseInt(cpf.charAt(i), 10) * (10 - i);
  let resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf.charAt(9), 10)) return false;
  soma = 0;
  for (let i = 0; i < 10; i++) soma += parseInt(cpf.charAt(i), 10) * (11 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  return resto === parseInt(cpf.charAt(10), 10);
};

let whatsappPopupRef = null;
const openWhatsAppPopup = (ddi, telefone) => {
  const numeroLimpo = telefone.replace(/\D/g, "");
  const ddiLimpo = (ddi || "+55").replace(/\D/g, "");
  const url = `https://wa.me/${ddiLimpo}${numeroLimpo}`;
  if (whatsappPopupRef && !whatsappPopupRef.closed) {
    whatsappPopupRef.location.href = url;
    whatsappPopupRef.focus();
  } else {
    const w = 400, h = 500;
    const left = window.screen.width - w - 20;
    const top = window.screen.height - h - 100;
    whatsappPopupRef = window.open(url, "whatsapp_popup", `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
  }
};

// ============================================================================
// Ordenação de resultados: ATIVOS no topo, BLOQUEADOS/VENCIDOS/INATIVOS abaixo.
// Isso agiliza a decisão do porteiro em /cliente/buscar — os cadastros que
// podem entrar/sair aparecem primeiro; os bloqueados descem para consulta.
// A lógica de "bloqueio" reflete exatamente o `bloqueio` calculado dentro
// dos componentes MoradorCard e VisitanteCard.
// ============================================================================
const _isMoradorBloqueado = (m) => {
  if (!m) return false;
  // v3.11.0 — Se o backend enviou `bloqueio_efetivo`, usa direto (considera
  // cobertura por CPF: linha principal com autorizador caído NÃO é bloqueada
  // se outra EQL do CPF está habilitada — exibe apenas warning).
  if (typeof m.bloqueio_efetivo === "boolean") return m.bloqueio_efetivo;
  // v1.9.0 — Autorização adicional bloqueada tem prioridade máxima
  const asit = m.autorizacao_situacao;
  if (asit && asit.tipo && asit.tipo !== "ok") return true;
  if ((m.motivo_bloqueio || "").trim()) return true;
  const vf = m.validade_final;
  if (vf) {
    try {
      const s = String(vf);
      const iso = s.length === 10 ? `${s}T23:59:59` : (s.length === 16 ? `${s}:00` : s);
      const d = new Date(iso);
      if (!isNaN(d.getTime()) && new Date() > d) return true;
    } catch { /* ignora */ }
  }
  if (m.ativo === false) return true;
  const sit = m.autorizador_situacao;
  if (sit && sit.tipo && sit.tipo !== "ok") return true;
  return false;
};

const _isVisitanteBloqueado = (v) => {
  if (!v) return false;
  // Herança de bloqueio da pessoa_visitada (backend enriquece este campo)
  const pv = v.pessoa_visitada_situacao;
  if (pv && pv.tipo && pv.tipo !== "ok") return true;
  if (v.ciclo_finalizado) return true;
  const qtdeEntradas = v.qtde_entradas ?? (v.data_hora_entrada ? 1 : 0);
  const qtdeSaidas = v.qtde_saidas ?? (v.data_hora_saida ? 1 : 0);
  const agora = new Date();
  if (!v.permanencia_inicial && !v.permanencia_final) {
    // Sem datas → 1 ciclo único; expira após 1ª saída
    if (qtdeSaidas >= 1) return true;
  } else {
    if (v.permanencia_inicial) {
      const pi = new Date(v.permanencia_inicial);
      if (!isNaN(pi.getTime()) && agora < pi) return true;
    }
    if (v.permanencia_final) {
      const pf = new Date(v.permanencia_final);
      if (!isNaN(pf.getTime()) && agora > pf) return true;
    }
  }
  return false;
};

// Ordenação estável: ativos primeiro (0), bloqueados depois (1). Preserva ordem
// original entre pares do mesmo grupo (por causa do sort estável do V8).
const sortMoradoresAtivosPrimeiro = (arr) =>
  Array.isArray(arr) ? [...arr].sort((a, b) => Number(_isMoradorBloqueado(a)) - Number(_isMoradorBloqueado(b))) : [];
const sortVisitantesAtivosPrimeiro = (arr) =>
  Array.isArray(arr) ? [...arr].sort((a, b) => Number(_isVisitanteBloqueado(a)) - Number(_isVisitanteBloqueado(b))) : [];

// ============================================================================
// Geração de impressão / PDF dos resultados de pesquisa
// Abre uma nova janela com HTML formatado para impressão (zero dependências).
// ============================================================================
const esc = (s) => String(s ?? "").replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" })[c]);
const fmtDtPt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

const imprimirResultados = (resultado, tipoBusca, termo) => {
  if (!resultado) return;
  const cli = (() => {
    try { return JSON.parse(sessionStorage.getItem("brave_cliente_data") || "{}"); } catch { return {}; }
  })();
  const clienteNome = cli.nome_fantasia || cli.razao_social || cli.cliente || "";
  const apelido = cli.apelido_portaria || cli.apelido || "";
  const operadorNome = getOperadorNome();

  const rows = (arr, cols) => arr.map((it) => `<tr>${cols.map((c) => `<td>${esc(typeof c === "function" ? c(it) : it[c])}</td>`).join("")}</tr>`).join("");

  const moradoresHtml = resultado.moradores?.length
    ? `
      <h2>Moradores / Funcionários (${resultado.moradores.length})</h2>
      <table>
        <thead><tr><th>Nome</th><th>CPF</th><th>EQL</th><th>Cargo / Setor</th><th>Placa</th><th>Validade</th></tr></thead>
        <tbody>${rows(resultado.moradores, [
          (m) => {
            const adicionais = Array.isArray(m.funcoes_adicionais_descricoes) ? m.funcoes_adicionais_descricoes : [];
            if (!adicionais.length) return m.nome || "";
            const tags = [m.funcao_descricao || "", ...adicionais.map((fd) => `+${fd}`)].filter(Boolean).join(" ");
            return `${m.nome || ""} • ${tags}`;
          },
          "cpf", "eql",
          (m) => [m.funcao_nome, m.departamento_nome].filter(Boolean).join(" / "),
          (m) => m.placa || (m.veiculos || []).map((v) => v.placa).join(", "),
          (m) => fmtDtPt(m.validade) || (m.validade_status === "vencido" ? "VENCIDO" : "")
        ])}</tbody>
      </table>` : "";

  const visitantesHtml = resultado.visitantes?.length
    ? `
      <h2>Visitantes (${resultado.visitantes.length})</h2>
      <table>
        <thead><tr><th>Nome</th><th>CPF</th><th>Empresa</th><th>Visitado</th><th>EQL</th><th>Placa</th><th>Entrada</th><th>Saída</th></tr></thead>
        <tbody>${rows(resultado.visitantes, [
          "nome", "cpf", "empresa_nome", "pessoa_visitada_nome", "unidade",
          (v) => v.placa || "",
          (v) => fmtDtPt(v.data_hora_entrada || v.entrada || v.created_at),
          (v) => fmtDtPt(v.data_hora_saida || v.saida)
        ])}</tbody>
      </table>` : "";

  const agendasHtml = resultado.agendas?.length
    ? `
      <h2>Agendas (${resultado.agendas.length})</h2>
      <table>
        <thead><tr><th>Nome</th><th>CPF</th><th>Empresa</th><th>Visitado</th><th>EQL</th><th>Placa</th><th>Início</th><th>Fim</th></tr></thead>
        <tbody>${rows(resultado.agendas, [
          "nome", "cpf", "empresa_nome", "pessoa_visitada_nome", "unidade",
          (a) => a.placa || "",
          (a) => fmtDtPt(a.permanencia_inicial),
          (a) => fmtDtPt(a.permanencia_final)
        ])}</tbody>
      </table>` : "";

  const labelTipo = { placa: "Placa", nome: "Nome", cpf: "CPF", eql: "EQL", lca: "Local de Acesso", empresa: "Empresa" }[tipoBusca] || tipoBusca;
  const html = `
<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"><title>Resultado da Pesquisa — ${esc(clienteNome)}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:24px;font-size:9pt}
  header{border-bottom:2px solid #1A3F75;padding-bottom:8px;margin-bottom:16px}
  header h1{margin:0;font-size:14pt;color:#1A3F75}
  header .meta{font-size:8pt;color:#555;margin-top:4px;display:flex;gap:16px;flex-wrap:wrap}
  h2{font-size:10pt;margin:18px 0 6px;color:#1A3F75;border-bottom:1px solid #ccc;padding-bottom:3px}
  table{width:100%;border-collapse:collapse;margin-bottom:8px}
  th,td{border:1px solid #d0d0d0;padding:3px 5px;font-size:8pt;text-align:left;vertical-align:top}
  th{background:#f0f4fa;font-weight:bold;color:#1A3F75}
  tbody tr:nth-child(even){background:#fafbfc}
  footer{margin-top:24px;font-size:7pt;color:#888;text-align:center;border-top:1px solid #eee;padding-top:6px}
  @media print{ body{margin:12mm} button{display:none} }
  .noprint{margin-bottom:12px}
  .noprint button{padding:6px 14px;font-size:9pt;cursor:pointer;background:#1A3F75;color:#fff;border:none;border-radius:4px}
</style></head>
<body>
  <div class="noprint"><button onclick="window.print()">Imprimir / Salvar como PDF</button></div>
  <header>
    <h1>Resultado da Pesquisa</h1>
    <div class="meta">
      <span><b>Cliente:</b> ${esc(clienteNome)}${apelido ? ` (${esc(apelido)})` : ""}</span>
      <span><b>Filtro:</b> ${esc(labelTipo)} = <b>${esc(termo)}</b></span>
      <span><b>Emitido em:</b> ${fmtDtPt(new Date().toISOString())}</span>
      <span><b>Emitido por:</b> ${esc(operadorNome)}</span>
      <span><b>Totais:</b> Moradores ${resultado.moradores?.length || 0} · Visitantes ${resultado.visitantes?.length || 0} · Agendas ${resultado.agendas?.length || 0}</span>
    </div>
  </header>
  ${moradoresHtml}${visitantesHtml}${agendasHtml}
  ${!(moradoresHtml || visitantesHtml || agendasHtml) ? "<p><i>Nenhum resultado encontrado.</i></p>" : ""}
  <footer>Brave Access · Relatório gerado em ${fmtDtPt(new Date().toISOString())}</footer>
  <script>setTimeout(()=>{ try { window.focus(); window.print(); } catch(e){} }, 300);</script>
</body></html>`;

  const w = window.open("", "imprimir_pesquisa", "width=1024,height=720");
  if (!w) {
    alert("Habilite pop-ups para imprimir os resultados.");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
};

export default function VeiculoPesquisaModal({ open, onOpenChange, asPage = false }) {
  const navigate = useNavigate();

  // Handler unificado para confirmar agenda (mesmo protocolo do AgendasHojeModal)
  const confirmarAgendaFromBusca = async (agenda) => {
    if (!agenda?.id) return;
    // Se a agenda não tem CPF válido, abre mini-modal de captura antes de confirmar
    const cpfDig = (agenda.cpf || "").replace(/\D/g, "");
    if (cpfDig.length !== 11) {
      setCpfPromptAgenda({ agenda, cpfValor: "" });
      return;
    }
    try {
      let confirmacaoManual = false;
      try {
        const r = await clienteApi.get("/cliente/configuracoes");
        confirmacaoManual = r.data?.confirmacao_agenda_manual === true;
      } catch {
        // Falha → assume automático
      }
      if (confirmacaoManual) {
        navigate(`/cliente/visitantes/novo?from_agenda=${agenda.id}`);
        onOpenChange && onOpenChange(false);
        return;
      }
      await clienteApi.post(`/cliente/agendas/${agenda.id}/confirmar`);
      toast.success("Agenda confirmada como visitante!");
      // Atualiza a lista local removendo a agenda confirmada
      setResultado((prev) => prev ? { ...prev, agendas: prev.agendas.filter((a) => a.id !== agenda.id) } : prev);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao confirmar agenda");
    }
  };

  const salvarCpfNaAgenda = async () => {
    if (!cpfPromptAgenda) return;
    const cpfDig = (cpfPromptAgenda.cpfValor || "").replace(/\D/g, "");
    if (cpfDig.length !== 11) { toast.error("Informe os 11 dígitos do CPF"); return; }
    if (!isCpfValido(cpfDig)) { toast.error("CPF inválido — verifique os dígitos"); return; }
    setSavingCpfPromptAgenda(true);
    try {
      const resp = await clienteApi.put(`/cliente/agendas/${cpfPromptAgenda.agenda.id}`, { cpf: cpfDig });
      const ag = resp?.data || {};
      const nomeFinal = ag.nome || cpfPromptAgenda.agenda.nome;
      const nomeAnterior = cpfPromptAgenda.agenda.nome;
      if (nomeFinal && nomeAnterior && nomeFinal.trim().toUpperCase() !== nomeAnterior.trim().toUpperCase()) {
        toast.success(`CPF gravado. Nome ajustado para "${nomeFinal}" (cadastro anterior)`);
      } else {
        toast.success("CPF gravado na agenda");
      }
      const agId = cpfPromptAgenda.agenda.id;
      setResultado((prev) => prev ? {
        ...prev,
        agendas: prev.agendas.map((a) => a.id === agId ? { ...a, cpf: cpfDig, nome: nomeFinal } : a),
      } : prev);
      setCpfPromptAgenda(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao gravar CPF");
    } finally {
      setSavingCpfPromptAgenda(false);
    }
  };
  const [placa, setPlaca] = useState("");
  // v1.7.0 — Reconhecimento de voz (Web Speech API). Suportado em Chrome,
  // Edge, Safari (macOS/iOS 14.5+) e navegadores baseados em Chromium. Se
  // o navegador não suportar, o botão do microfone fica desabilitado.
  const [ouvindo, setOuvindo] = useState(false);
  const recogRef = useRef(null);
  const speechSupported = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const [tipoBusca, setTipoBusca] = useState("placa"); // placa | nome | cpf
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  // v3.10.0 (Fase 4) — Mapa {cpfNormalizado: {total, hoje}} de contadores
  // de observações-CPF para os moradores retornados na busca. Populado
  // por batch quando `resultado.moradores` chega. Usado pelo MoradorCard
  // para renderizar o badge "📝 OBS HOJE (N)" (âmbar) ou "📝 OBS (N)"
  // (cinza discreto) ao lado do nome do morador.
  const [obsCountsMap, setObsCountsMap] = useState({});
  const [openSections, setOpenSections] = useState({ moradores: true, visitantes: false, agendas: false, deletados: false });
  const [deletados, setDeletados] = useState([]);
  const [cpfPromptAgenda, setCpfPromptAgenda] = useState(null); // { agenda, cpfValor }
  const [savingCpfPromptAgenda, setSavingCpfPromptAgenda] = useState(false);
  const debounceTimer = useRef(null);
  const lastQueryRef = useRef({ termo: "", tipo: "placa", modo: "completa" });
  const inputRef = useRef(null);
  const [cpfInvalidoOpen, setCpfInvalidoOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);

  // Modo da listagem de visitantes: completa | distintos
  // Persistido por máquina (todos os operadores que logarem nessa estação herdam).
  const [modoBusca, setModoBusca] = useState(() => localStorage.getItem("brave_busca_modo") || "completa");
  // v3.13.6 — Filtro do collapsible "Usuários Fixos" por tipo_cartao:
  //   • todos      → sem filtro (default)
  //   • moradores  → tipo_cartao ∈ {16,21} (Moradores e Familiares)
  //   • agregados  → tipo_cartao ∉ {16,21}
  // Decora a última seleção em localStorage para próximas buscas.
  const [filtroFixos, setFiltroFixos] = useState(() => localStorage.getItem("brave_busca_filtro_fixos") || "todos");
  useEffect(() => { localStorage.setItem("brave_busca_modo", modoBusca); }, [modoBusca]);

  // Modal de Observações/Saída do visitante (mesmo modal da grid)
  const [obsVisitante, setObsVisitante] = useState(null);
  const [obsModalOpen, setObsModalOpen] = useState(false);
  // Modal de Histórico de Movimentos (multi-ciclo: hóspedes que entram/saem várias vezes)
  const [movVisitante, setMovVisitante] = useState(null);
  const [movModalOpen, setMovModalOpen] = useState(false);
  // Modal customizado de Finalizar Ciclo (com campo de observação)
  const [finalizarVisitante, setFinalizarVisitante] = useState(null);
  const [finalizarOpen, setFinalizarOpen] = useState(false);

  // Confirmação do Finalizar Ciclo via modal customizado (recebe obs do textarea)
  const executarFinalizarCiclo = async (visitante, obs) => {
    try {
      const r = await clienteApi.post(
        `/cliente/visitantes/${visitante.id}/finalizar-ciclo`,
        { obs: obs || "Finalização manual pelo operador" }
      );
      toast.success("Ciclo encerrado com sucesso");
      const upd = r.data?.visitante;
      if (upd) {
        setResultado((prev) => prev && {
          ...prev,
          visitantes: prev.visitantes.map((it) => it.id === upd.id ? { ...it, ...upd } : it)
        });
        setMovVisitante((cur) => cur && cur.id === upd.id ? { ...cur, ...upd } : cur);
      }
      setFinalizarOpen(false);
      setFinalizarVisitante(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao finalizar ciclo");
    }
  };

  // Handler reutilizável: abre o modal customizado de Finalizar (usado no card e no modal de histórico)
  const handleFinalizarCiclo = (visitante) => {
    setFinalizarVisitante(visitante);
    setFinalizarOpen(true);
  };

  // Após registrar um acesso, limpa a tela e o campo de busca para o porteiro
  // já digitar a próxima placa sem precisar limpar manualmente.
  const handleAcessoRegistrado = () => {
    setPlaca("");
    setResultado(null);
    lastQueryRef.current = { termo: "", tipo: tipoBusca, modo: modoBusca };
    try {
      sessionStorage.removeItem("brave_ultima_placa");
      window.dispatchEvent(new Event("brave_placa_update"));
    } catch { /* ignore */ }
    setTimeout(() => { try { inputRef.current?.focus(); } catch { /* ignore */ } }, 60);
  };
  // Modal de ajuda do select Modo de busca
  const [modoHelpOpen, setModoHelpOpen] = useState(false);
  // Modal de registro manual de acesso (Usuário Fixo)
  const [acessoFixoOpen, setAcessoFixoOpen] = useState(false);
  const [acessoFixoMorador, setAcessoFixoMorador] = useState(null);
  const [acessoFixoPlacaOverride, setAcessoFixoPlacaOverride] = useState(null);
  const [agendaFormOpen, setAgendaFormOpen] = useState(false);
  const [agendaFormInitialCpf, setAgendaFormInitialCpf] = useState("");

  // Chave de configuração por usuário (codigo_origem + login) — evita compartilhar
  // preferência entre operadores que usem a mesma máquina.
  const buscaConfigKey = (() => {
    try {
      const c = JSON.parse(sessionStorage.getItem("brave_cliente_data") || "{}");
      const cod = c?.codigo_origem ?? "x";
      const login = c?.login_cliente ?? c?.login ?? "x";
      return `brave_busca_auto_expand:${cod}:${login}`;
    } catch {
      return "brave_busca_auto_expand:x:x";
    }
  })();
  const [autoExpand, setAutoExpand] = useState(() => {
    const v = localStorage.getItem(buscaConfigKey);
    return v === null ? true : v === "true";
  });
  useEffect(() => {
    localStorage.setItem(buscaConfigKey, String(autoExpand));
  }, [autoExpand, buscaConfigKey]);

  // Em modo página, o componente sempre está "aberto"
  const isOpen = asPage ? true : open;

  // Função genérica de "Incluir" usada nos 3 collapsibles vazios.
  // - Se tipo for CPF e o CPF for inválido → abre popup central e retorna sem agir.
  // - Senão → copia o termo, exibe toast e (se tiver rota) navega.
  const handleIncluirVazio = async (rota) => {
    const valor = (placa || "").trim();
    if (tipoBusca === "cpf" && !isCpfValido(valor)) {
      setCpfInvalidoOpen(true);
      return;
    }
    const labelTipo = tipoBusca === "cpf" ? "CPF" : tipoBusca === "placa" ? "Placa" : "Nome";
    if (valor) {
      try {
        await navigator.clipboard.writeText(valor);
        toast.success(`${labelTipo} copiad${tipoBusca === "placa" ? "a" : "o"} para área de transferência`);
      } catch {
        toast.error("Não foi possível copiar para a área de transferência");
      }
    }
    if (rota) {
      if (!asPage) onOpenChange(false);
      navigate(rota);
    }
  };

  const handleIncluirAgendaVazia = async () => {
    const valor = (placa || "").trim();
    if (valor) {
      try {
        await navigator.clipboard.writeText(valor);
        const labelTipo = tipoBusca === "cpf" ? "CPF" : tipoBusca === "placa" ? "Placa" : "Nome";
        toast.success(`${labelTipo} copiad${tipoBusca === "placa" ? "a" : "o"} para área de transferência`);
      } catch {
        // silencioso — abrir o modal mesmo sem conseguir copiar
      }
    }
    setAgendaFormOpen(true);
  };

  // Detecta se é PC (não mobile/tablet) — só PC tem ponteiro fino e hover real
  const isDesktop = () => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  };

  // Quando o tipo de busca muda (radio), foca o input apenas em PC
  useEffect(() => {
    if (!isOpen) return;
    if (!isDesktop()) return;
    const t = setTimeout(() => {
      try { inputRef.current?.focus(); } catch { /* ignore */ }
    }, 50);
    return () => clearTimeout(t);
  }, [tipoBusca, isOpen]);

  const toggleSection = (key) => setOpenSections((p) => ({ ...p, [key]: !p[key] }));

  // v2 (Feb/2026) — Se a página for aberta com `?placa=XXX` na URL,
  // pré-preenche e dispara a busca automaticamente. Usado pelo handoff
  // do BSS-Portaria quando a placa reconhecida já está cadastrada.
  useEffect(() => {
    if (!asPage) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const p = params.get("placa");
      if (p && p.trim()) {
        setPlaca(p.trim().toUpperCase());
        setTipoBusca("placa");
        // Limpa o param da URL para não repetir ao navegar
        try { window.history.replaceState(null, "", window.location.pathname); }
        catch { /* ignore */ }
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPage]);

  const pesquisar = useCallback(async () => {
    const val = placa.trim();
    const minLen = 3;
    if (val.length < minLen) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ termo: val, tipo: tipoBusca, modo: modoBusca });
      // Mantém compat com o nome legado quando for busca por placa
      if (tipoBusca === "placa") params.set("placa", val);
      const res = await clienteApi.get(`/cliente/veiculos/pesquisar?${params.toString()}`);
      setResultado(res.data);
      setOpenSections({ moradores: autoExpand, visitantes: autoExpand, agendas: autoExpand, deletados: false });
      lastQueryRef.current = { termo: val, tipo: tipoBusca, modo: modoBusca };
      sessionStorage.setItem("brave_ultima_placa", val);
      window.dispatchEvent(new Event("brave_placa_update"));
      // Busca paralela em cadastros EXCLUÍDOS/REVOGADOS (audit log) — não bloqueia
      // o resultado principal; alimenta o collapsible "Deletados".
      try {
        const rd = await clienteApi.get("/cliente/deletados/buscar", { params: { q: val } });
        const dl = rd.data?.items || [];
        setDeletados(dl);
        // Destaque: abre automaticamente quando há registro de exclusão/revogação
        if (dl.length > 0) setOpenSections((p) => ({ ...p, deletados: true }));
      } catch { setDeletados([]); }
    } catch {
      setResultado(null);
      setDeletados([]);
      sessionStorage.setItem("brave_ultima_placa", val);
      window.dispatchEvent(new Event("brave_placa_update"));
    } finally {
      setLoading(false);
    }
  }, [placa, tipoBusca, modoBusca, autoExpand]);

  // v3.10.0 (Fase 4) — Batch count de observações-CPF para os moradores
  // exibidos. Executa 1 única chamada agrupando todos os CPFs (aggregation
  // no backend). O map alimenta o badge do MoradorCard.
  useEffect(() => {
    const cpfs = (resultado?.moradores || [])
      .map((m) => m?.cpf)
      .filter((c) => c && String(c).replace(/\D/g, "").length > 0);
    if (cpfs.length === 0) {
      setObsCountsMap({});
      return;
    }
    let cancelado = false;
    (async () => {
      try {
        const r = await clienteApi.post("/cliente/observacoes-cpf/count-batch", { cpfs });
        if (!cancelado) setObsCountsMap(r.data || {});
      } catch { /* badge é bônus visual — falha silenciosa */ }
    })();
    return () => { cancelado = true; };
  }, [resultado]);

  // Debounce automático de 350ms — busca conforme o operador digita (3+ chars)
  useEffect(() => {
    if (!isOpen) return;
    // LCA (Local de Acesso/Logradouro): NÃO faz busca live — só no Enter/clique.
    // "Rua" ou "Rua 29" poderiam listar centenas de cadastros à toa.
    if (tipoBusca === "lca") return;
    const val = placa.trim();
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (val.length < 3) {
      // Limpa resultado quando o usuário apaga o texto pra menos de 3 chars
      if (resultado && val.length === 0) setResultado(null);
      return;
    }
    // Evita repetir a mesma query (termo + tipo + modo)
    if (
      lastQueryRef.current.termo === val &&
      lastQueryRef.current.tipo === tipoBusca &&
      lastQueryRef.current.modo === modoBusca
    ) return;
    debounceTimer.current = setTimeout(() => {
      pesquisar();
    }, 350);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placa, tipoBusca, modoBusca, isOpen]);

  const limpar = () => {
    setPlaca("");
    setResultado(null);
    setDeletados([]);
    lastQueryRef.current = { termo: "", tipo: tipoBusca };
  };

  // Ao trocar o tipo de busca, preserva o texto digitado (UX melhor — o operador
  // pode estar refinando a busca e errou o radio). Apenas o resultado é zerado.
  const handleChangeTipo = (novoTipo) => {
    setTipoBusca(novoTipo);
    setResultado(null);
    lastQueryRef.current = { termo: placa, tipo: novoTipo };
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") pesquisar();
  };

  // v1.7.0 — Handler do microfone: usa Web Speech API para transcrever a fala
  // do operador e injetar no input de busca já normalizado pelo `cfg.normalize`.
  // Voz é reconhecida em pt-BR; para busca por placa/CPF/EQL removemos espaços
  // e pontuação que a API tende a inserir ("A B C 1 D 2 3" → "ABC1D23").
  const toggleEscuta = () => {
    if (!speechSupported) {
      toast.error("Reconhecimento de voz não suportado neste navegador");
      return;
    }
    // Se já está gravando, para.
    if (recogRef.current && ouvindo) {
      try { recogRef.current.stop(); } catch { /* ignore */ }
      setOuvindo(false);
      return;
    }
    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Ctor();
    rec.lang = "pt-BR";
    rec.continuous = false;
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onstart = () => setOuvindo(true);
    rec.onend = () => setOuvindo(false);
    rec.onerror = (e) => {
      setOuvindo(false);
      if (e?.error === "not-allowed") {
        toast.error("Permita o acesso ao microfone para usar a busca por voz");
      } else if (e?.error === "no-speech") {
        toast.warning("Nada foi detectado no microfone — tente novamente");
      } else {
        toast.error(`Falha no reconhecimento de voz: ${e?.error || "desconhecido"}`);
      }
    };
    rec.onresult = (evt) => {
      try {
        const transcript = evt.results?.[0]?.[0]?.transcript || "";
        // Normaliza conforme o tipo de busca — para placa/cpf/eql cortamos espaços
        // e pontuação; para nome/empresa deixamos o texto natural.
        let bruto = transcript;
        if (["placa", "cpf", "eql"].includes(tipoBusca)) {
          bruto = transcript.replace(/[\s.\-,;!?]/g, "");
        }
        const valor = cfg.normalize(bruto);
        setPlaca(valor);
        toast.success(`Ouvi: "${transcript}"`);
      } catch (err) {
        console.error("Speech onresult err:", err);
      }
    };
    recogRef.current = rec;
    try {
      rec.start();
    } catch (err) {
      console.error("start error", err);
      setOuvindo(false);
    }
  };

  const handleClose = (v) => {
    if (!v) {
      setPlaca("");
      setResultado(null);
      setTipoBusca("placa");
      lastQueryRef.current = { termo: "", tipo: "placa" };
    }
    onOpenChange(v);
  };

  // Configuração da exibição conforme o tipo
  const cfg = (() => {
    if (tipoBusca === "nome") {
      return {
        placeholder: "Informe o nome (ou parte) do morador, visitante ou agenda...",
        maxLength: 60,
        normalize: (v) => v,
        inputClasses: "bg-brand-dark/60 border-brand-medium/30 text-white placeholder:text-brand-ice/40 focus:border-brand-light/50 pr-8",
      };
    }
    if (tipoBusca === "cpf") {
      return {
        placeholder: "Informe o CPF (somente números)...",
        maxLength: 14,
        normalize: (v) => v.replace(/[^0-9.\-]/g, ""),
        inputClasses: "bg-brand-dark/60 border-brand-medium/30 text-white placeholder:text-brand-ice/40 focus:border-brand-light/50 font-mono pr-8",
      };
    }
    if (tipoBusca === "empresa") {
      return {
        placeholder: "Informe a empresa prestadora (ou parte do nome)...",
        maxLength: 60,
        normalize: (v) => v,
        inputClasses: "bg-brand-dark/60 border-brand-medium/30 text-white placeholder:text-brand-ice/40 focus:border-brand-light/50 pr-8",
      };
    }
    if (tipoBusca === "eql") {
      return {
        placeholder: "Informe a EQL (ou parte) — moradores e visitantes...",
        maxLength: 30,
        normalize: (v) => v,
        inputClasses: "bg-brand-dark/60 border-brand-medium/30 text-white placeholder:text-brand-ice/40 focus:border-brand-light/50 pr-8",
      };
    }
    if (tipoBusca === "lca") {
      return {
        placeholder: "Logradouro ou parte (ex.: 1300 → Avenida 1300) · casa: Rua 29, 470 · Enter/Buscar",
        maxLength: 60,
        normalize: (v) => v,
        inputClasses: "bg-brand-dark/60 border-brand-medium/30 text-white placeholder:text-brand-ice/40 focus:border-brand-light/50 pr-8",
      };
    }
    return {
      placeholder: "Informe a placa do veículo...",
      maxLength: 10,
      normalize: (v) => v.replace(/[^a-zA-Z0-9]/g, "").toUpperCase(),
      inputClasses: "bg-brand-dark/60 border-brand-medium/30 text-white placeholder:text-brand-ice/40 focus:border-brand-light/50 font-mono uppercase pr-8",
    };
  })();

  // Wrapper do conteúdo — mesmo JSX usado em modal e página
  const renderContent = () => (
    <>
      {/* Campo de pesquisa */}
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative flex-1 max-w-md min-w-[220px]">
            <Input
              ref={inputRef}
              data-testid="veiculo-pesquisa-input"
              placeholder={cfg.placeholder}
              value={placa}
              onChange={(e) => setPlaca(cfg.normalize(e.target.value))}
              onKeyDown={handleKeyDown}
              className={`${cfg.inputClasses} pr-16`}
              maxLength={cfg.maxLength}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {/* Microfone — busca por voz (Web Speech API) */}
              <button
                type="button"
                data-testid="veiculo-pesquisa-microfone"
                onClick={toggleEscuta}
                disabled={!speechSupported}
                title={
                  !speechSupported
                    ? "Reconhecimento de voz não suportado neste navegador"
                    : ouvindo ? "Toque para parar" : "Falar para buscar"
                }
                className={`p-1 rounded transition-colors ${
                  !speechSupported
                    ? "text-brand-ice/20 cursor-not-allowed"
                    : ouvindo
                      ? "text-red-400 animate-pulse hover:text-red-300"
                      : "text-brand-ice/50 hover:text-brand-light"
                }`}
              >
                {ouvindo ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              {placa && (
                <button
                  data-testid="veiculo-pesquisa-limpar"
                  onClick={limpar}
                  className="text-brand-ice/40 hover:text-brand-ice transition-colors p-1"
                  title="Limpar"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <button
            data-testid="veiculo-pesquisa-btn"
            onClick={pesquisar}
            disabled={loading || placa.trim().length < 3}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-brand-ice border border-brand-ice/20 text-xs font-heading transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Search className="w-4 h-4" />
            {loading ? "..." : "Buscar"}
          </button>
          {/* v3.13.7 — Modo (Lista Completa | Distintos) movido pra linha de cima,
              alinhado à direita, antes do select "Fixos". Afeta apenas a listagem
              de Visitantes. */}
          <div className="flex items-center gap-1 ml-auto">
            <Select value={modoBusca} onValueChange={setModoBusca}>
              <SelectTrigger
                data-testid="busca-modo-select"
                className="h-9 w-[160px] bg-brand-dark/60 border-brand-medium/30 text-brand-ice text-xs px-2"
                title="Modo de listagem dos visitantes"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="completa">Lista Completa</SelectItem>
                <SelectItem value="distintos">Distintos</SelectItem>
              </SelectContent>
            </Select>
            <button
              type="button"
              data-testid="busca-modo-help-btn"
              onClick={() => setModoHelpOpen(true)}
              title="Diferença entre Lista Completa e Distintos"
              className="p-1.5 rounded-md text-brand-ice/50 hover:text-brand-light hover:bg-white/10 transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </div>
          {/* v3.13.6 — Filtro do collapsible "Usuários Fixos" por tipo_cartao.
              Não refaz busca — filtra localmente o array de moradores. */}
          <div className="flex items-center gap-1.5">
            <label className="text-[11px] text-brand-ice/50 whitespace-nowrap">Fixos:</label>
            <Select
              value={filtroFixos}
              onValueChange={(v) => { setFiltroFixos(v); localStorage.setItem("brave_busca_filtro_fixos", v); }}
            >
              <SelectTrigger
                data-testid="veiculo-pesquisa-filtro-fixos"
                className="w-[210px] h-9 text-xs bg-brand-dark/60 border-brand-medium/30 text-brand-ice"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos" data-testid="filtro-fixos-todos">Todos</SelectItem>
                <SelectItem value="moradores" data-testid="filtro-fixos-moradores">Moradores e Familiares</SelectItem>
                <SelectItem value="agregados" data-testid="filtro-fixos-agregados">Agregados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Radio: tipo de busca */}
        <div className="flex items-center gap-4 mt-2 text-xs text-brand-ice/80" data-testid="veiculo-pesquisa-tipos">
          <span className="text-brand-ice/50">Buscar por:</span>
          {[
            { v: "placa", label: "Placa" },
            { v: "nome", label: "Nome" },
            { v: "cpf", label: "CPF" },
            { v: "eql", label: "EQL" },
            { v: "lca", label: "LCA", title: "Local de Acesso ou Logradouro" },
            { v: "empresa", label: "Empresa" },
          ].map((opt) => (
            <label
              key={opt.v}
              data-testid={`veiculo-pesquisa-tipo-${opt.v}`}
              title={opt.title || opt.label}
              className={`flex items-center gap-1.5 cursor-pointer select-none px-2 py-1 rounded-md transition-colors ${
                tipoBusca === opt.v ? "bg-brand-light/15 text-brand-ice" : "hover:bg-white/5"
              }`}
            >
              <input
                type="radio"
                name="tipo-busca-veiculo"
                value={opt.v}
                checked={tipoBusca === opt.v}
                onChange={() => handleChangeTipo(opt.v)}
                className="w-3.5 h-3.5 accent-brand-light cursor-pointer"
              />
              <span>{opt.label}</span>
            </label>
          ))}
          {/* Select Distintos | Lista Completa (afeta apenas a listagem de Visitantes) */}
          <div className="ml-2 flex items-center gap-2">
            {/* v3.13.7 — Select Modo (Distintos/Lista Completa) e Help foram
                movidos para a linha de cima (ao lado do input de busca). */}
          </div>
        </div>

        {/* Resultados */}
        {resultado && (
          <div className="space-y-3 mt-2">
            {/* Barra de ações dos resultados */}
            {(resultado.moradores.length + resultado.visitantes.length + resultado.agendas.length) > 0 && (
              <div className="flex justify-end">
                <button
                  type="button"
                  data-testid="busca-imprimir-btn"
                  onClick={() => imprimirResultados(resultado, tipoBusca, placa)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-brand-ice border border-brand-ice/20 text-xs font-heading transition"
                  title="Imprimir ou exportar resultados (Moradores, Visitantes, Agendas)"
                >
                  <Printer className="w-4 h-4" />
                  Imprimir / PDF
                </button>
              </div>
            )}

            {/* Collapsible Moradores */}
            <MoradoresCollapsible
              moradores={(() => {
                // v3.13.6 — Filtra por tipo_cartao antes de agrupar/renderizar.
                // NÃO refaz busca no backend — apenas oculta cards. O total
                // exibido no header do collapsible reflete o filtro atual.
                const src = resultado.moradores || [];
                if (filtroFixos === "todos") return src;
                const tc = (m) => Number(m?.tipo_cartao);
                if (filtroFixos === "moradores") return src.filter((m) => tc(m) === 16 || tc(m) === 21);
                if (filtroFixos === "agregados") return src.filter((m) => tc(m) !== 16 && tc(m) !== 21);
                return src;
              })()}
              obsCountsMap={obsCountsMap}
              open={openSections.moradores}
              onToggle={() => toggleSection("moradores")}
              placaBuscada={tipoBusca === "placa" ? placa : ""}
              filtroLabel={
                filtroFixos === "moradores" ? "Moradores e Familiares" :
                filtroFixos === "agregados" ? "Agregados" : null
              }
              onIncluir={() => handleIncluirVazio("/cliente/moradores/novo")}
              onCloseModal={() => { if (!asPage) onOpenChange(false); }}
              onRegistrarAcesso={(mor, placaOverride) => { setAcessoFixoMorador(mor); setAcessoFixoPlacaOverride(placaOverride || null); setAcessoFixoOpen(true); }}
            />

            {/* Collapsible Visitantes */}
            <Collapsible open={openSections.visitantes} onOpenChange={() => toggleSection("visitantes")}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-white/5 border border-brand-ice/10 hover:bg-white/10 transition-colors">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-pink-400" />
                  <span className="text-sm font-heading font-bold text-brand-ice">Visitantes</span>
                  <span className="text-[10px] bg-pink-500/20 text-pink-300 px-2 py-0.5 rounded-full font-bold">
                    {resultado.visitantes.length}
                  </span>
                </div>
                {openSections.visitantes ? <ChevronUp className="w-4 h-4 text-brand-ice/50" /> : <ChevronDown className="w-4 h-4 text-brand-ice/50" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 space-y-2">
                {resultado.visitantes.length === 0 ? (
                  <div className="flex items-center justify-between gap-3 py-2 px-1">
                    <p className="text-brand-ice/40 text-xs">Nenhum visitante encontrado</p>
                    <button
                      type="button"
                      data-testid="visitantes-vazio-incluir-btn"
                      onClick={() => handleIncluirVazio("/cliente/visitantes/novo")}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-pink-500/20 border border-pink-400/30 text-brand-ice hover:bg-pink-500/30 transition-colors text-xs font-heading font-bold"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Incluir
                    </button>
                  </div>
                ) : (() => {
                  const sortedV = sortVisitantesAtivosPrimeiro(resultado.visitantes);
                  const vAtivos = sortedV.filter((v) => !_isVisitanteBloqueado(v));
                  const vBloqueados = sortedV.filter((v) => _isVisitanteBloqueado(v));
                  const renderVisitante = (v) => (
                    <VisitanteCard
                      key={v.id}
                      visitante={v}
                      placaBuscada={tipoBusca === "placa" ? placa : ""}
                      onAcaoEntradaSaida={(visitante) => {
                        setObsVisitante(visitante);
                        setObsModalOpen(true);
                      }}
                      onEditar={(visitante) => {
                        if (!asPage) onOpenChange(false);
                        navigate(`/cliente/visitantes/${visitante.id}`);
                      }}
                      onVerMovimentos={(visitante) => {
                        setMovVisitante(visitante);
                        setMovModalOpen(true);
                      }}
                      onFinalizarCiclo={handleFinalizarCiclo}
                      onIncluir={(visitante) => {
                        if (!asPage) onOpenChange(false);
                        const cpfClean = (visitante.cpf || "").replace(/\D/g, "");
                        const docalt = visitante.documento_alternativo ? "&docalt=1" : "";
                        const docVal = visitante.documento_alternativo ? (visitante.cpf || "") : cpfClean;
                        navigate(`/cliente/visitantes/novo?doc=${encodeURIComponent(docVal)}${docalt}`);
                      }}
                    />
                  );
                  return (
                    <>
                      {vAtivos.map(renderVisitante)}
                      <SemAcessoGroup items={vBloqueados} renderCard={renderVisitante} testIdPrefix="visitantes" />
                    </>
                  );
                })()}
              </CollapsibleContent>
            </Collapsible>

            {/* Collapsible Agendas */}
            <Collapsible open={openSections.agendas} onOpenChange={() => toggleSection("agendas")}>
              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-white/5 border border-brand-ice/10 hover:bg-white/10 transition-colors">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-heading font-bold text-brand-ice">Agendas</span>
                  <span className="text-[10px] bg-green-500/20 text-green-300 px-2 py-0.5 rounded-full font-bold">
                    {resultado.agendas.length}
                  </span>
                </div>
                {openSections.agendas ? <ChevronUp className="w-4 h-4 text-brand-ice/50" /> : <ChevronDown className="w-4 h-4 text-brand-ice/50" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 space-y-2">
                {resultado.agendas.length === 0 ? (
                  <div className="flex items-center justify-between gap-3 py-2 px-1">
                    <p className="text-brand-ice/40 text-xs">Nenhuma agenda encontrada</p>
                    <button
                      type="button"
                      data-testid="agendas-vazio-incluir-btn"
                      onClick={handleIncluirAgendaVazia}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-green-500/20 border border-green-400/30 text-brand-ice hover:bg-green-500/30 transition-colors text-xs font-heading font-bold"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Incluir
                    </button>
                  </div>
                ) : (
                  resultado.agendas.map((a) => (
                    <AgendaCard
                      key={a.id}
                      agenda={a}
                      placaBuscada={tipoBusca === "placa" ? placa : ""}
                      onConfirmar={confirmarAgendaFromBusca}
                      onCriarNovaAgenda={(agenda) => {
                        // Agenda vencida → abre AgendasHojeModal em modo form com CPF pré-preenchido.
                        const cpfDig = (agenda?.cpf || "").replace(/\D/g, "");
                        setAgendaFormInitialCpf(cpfDig);
                        setAgendaFormOpen(true);
                      }}
                    />
                  ))
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* Collapsible Deletados / Revogados — cadastros que perderam acesso */}
            <Collapsible open={openSections.deletados} onOpenChange={() => toggleSection("deletados")}>
              <CollapsibleTrigger className={`flex items-center justify-between w-full p-3 rounded-lg border transition-colors ${deletados.length > 0 ? "bg-red-500/15 border-red-500/50 ring-2 ring-red-500/40 hover:bg-red-500/20" : "bg-red-500/5 border-red-500/20 hover:bg-red-500/10"}`} data-testid="deletados-collapsible-trigger">
                <div className="flex items-center gap-2">
                  {deletados.length > 0 ? <AlertTriangle className="w-4 h-4 text-red-400" /> : <UserX className="w-4 h-4 text-red-400" />}
                  <span className={`text-sm font-heading font-bold ${deletados.length > 0 ? "text-red-200" : "text-brand-ice"}`}>Deletados</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${deletados.length > 0 ? "bg-red-500/40 text-red-100 animate-pulse" : "bg-red-500/20 text-red-300"}`}>
                    {deletados.length}
                  </span>
                  {deletados.length > 0 && (
                    <span className="text-[10px] text-red-300/90 font-bold uppercase tracking-wide">Atenção — placa/CPF excluído</span>
                  )}
                </div>
                {openSections.deletados ? <ChevronUp className="w-4 h-4 text-brand-ice/50" /> : <ChevronDown className="w-4 h-4 text-brand-ice/50" />}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 space-y-2">
                {deletados.length === 0 ? (
                  <p className="text-brand-ice/40 text-xs py-2 px-1">Nenhum cadastro excluído/revogado encontrado para esta busca</p>
                ) : (
                  deletados.map((d) => {
                    const opLabel = d.operacao === "revogar" ? "Revogado (expurgo total)"
                      : d.operacao === "baixa_eql" ? "Baixa de EQL"
                      : d.operacao === "placa_excluida" ? "Placa excluída"
                      : "Autorização excluída";
                    const cpfFmt = (() => {
                      const n = String(d.cpf || "").replace(/\D/g, "").slice(0, 11);
                      if (n.length !== 11) return d.cpf || "";
                      return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9)}`;
                    })();
                    const dataFmt = (() => {
                      if (!d.data_exclusao) return "—";
                      try {
                        const dt = new Date(d.data_exclusao);
                        if (!isNaN(dt.getTime())) {
                          const p = (x) => String(x).padStart(2, "0");
                          return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()} ${p(dt.getHours())}:${p(dt.getMinutes())}`;
                        }
                      } catch { /* */ }
                      return d.data_exclusao;
                    })();
                    return (
                      <div key={d.id} className="rounded-lg bg-red-500/5 border border-red-500/20 p-3" data-testid="deletado-card">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-heading font-bold text-brand-ice">{d.nome || cpfFmt || "—"}</span>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full border border-red-500/30 bg-red-500/15 text-red-300 font-bold">
                                <UserX className="w-3 h-3" />{opLabel}
                              </span>
                            </div>
                            <div className="text-[11px] text-brand-ice/50 mt-0.5">
                              {cpfFmt && <span>CPF {cpfFmt}</span>}
                              {d.matricula ? <span> · matr. {d.matricula}</span> : null}
                              {d.eql ? <span> · EQL {d.eql}</span> : null}
                            </div>
                          </div>
                          <div className="text-[11px] text-red-300/80 text-right whitespace-nowrap">
                            Excluído em<br /><b>{dataFmt}</b>
                            {d.operador_nome ? <div className="text-brand-ice/40">por {d.operador_nome}</div> : null}
                          </div>
                        </div>
                        {(d.placas && d.placas.length > 0) && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {d.placas.map((p) => (
                              <span key={p} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 text-[11px] font-mono">
                                <Car className="w-3 h-3" />{p}
                              </span>
                            ))}
                          </div>
                        )}
                        {d.motivo && (
                          <div className="mt-2 text-xs text-brand-ice/70 bg-white/5 rounded px-2 py-1.5">
                            <span className="text-brand-ice/40">Motivo: </span>{d.motivo}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {/* Estado vazio */}
        {!resultado && !loading && (
          <div className="text-center py-8">
            <Search className="w-12 h-12 text-brand-ice/20 mx-auto mb-3" />
            <p className="text-brand-ice/40 text-sm">
              {tipoBusca === "placa" && "Informe uma placa para pesquisar"}
              {tipoBusca === "nome" && "Informe um nome para pesquisar"}
              {tipoBusca === "cpf" && "Informe um CPF para pesquisar"}
              {tipoBusca === "eql" && "Informe a EQL para pesquisar"}
              {tipoBusca === "lca" && "Informe o Local de Acesso ou Logradouro (opcional: , número da casa) e clique Buscar"}
              {tipoBusca === "empresa" && "Informe a empresa prestadora para pesquisar"}
            </p>
          </div>
        )}
    </>
  );

  // Quando usado como PÁGINA: renderiza o conteúdo dentro do layout da página
  if (asPage) {
    return (
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-brand-ice tracking-tight text-shadow-logo flex items-center gap-3">
              <Search className="w-7 h-7" />
              Buscar Cadastro
            </h1>
            <p className="text-brand-ice/60 text-sm mt-1 font-body text-shadow-light">
              {tipoBusca === "placa" && "Informe a placa para localizar o proprietário"}
              {tipoBusca === "nome" && "Busque moradores, visitantes ou agendas pelo nome"}
              {tipoBusca === "cpf" && "Busque moradores, visitantes ou agendas pelo CPF"}
            </p>
          </div>
          <button
            type="button"
            data-testid="busca-config-btn"
            onClick={() => setConfigOpen(true)}
            title="Configurações da busca"
            className="flex-shrink-0 p-2 rounded-lg text-brand-ice/60 hover:text-brand-light hover:bg-white/10 transition-colors"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
        <div className="rounded-xl bg-white/5 backdrop-blur-sm border border-brand-ice/10 p-4 sm:p-6">
          {renderContent()}
        </div>

        {/* Modal — Configurações da busca */}
        <Dialog open={configOpen} onOpenChange={setConfigOpen}>
          <DialogContent data-testid="busca-config-dialog" className="bg-brand-dark/95 border-brand-ice/20 text-brand-ice max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-brand-ice font-heading">
                <Settings className="w-5 h-5" />
                Configurações da busca
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex items-start justify-between gap-4 rounded-lg bg-white/5 border border-brand-ice/10 p-3">
                <div className="flex-1">
                  <p className="text-sm font-heading font-bold text-brand-ice">Expandir resultados automaticamente</p>
                  <p className="text-xs text-brand-ice/60 mt-0.5">
                    Quando ligado, os collapsibles de Usuários Fixos, Visitantes e Agendas abrem juntos após a busca. Quando desligado, os resultados são carregados, mas permanecem fechados — você vê apenas as quantidades e decide qual abrir.
                  </p>
                </div>
                <Switch
                  data-testid="busca-config-auto-expand-switch"
                  checked={autoExpand}
                  onCheckedChange={setAutoExpand}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Popup central — CPF inválido */}
        <AlertDialog open={cpfInvalidoOpen} onOpenChange={setCpfInvalidoOpen}>
          <AlertDialogContent data-testid="cpf-invalido-dialog" className="bg-brand-dark/95 border-red-500/40 text-brand-ice">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-400 font-heading">CPF inválido</AlertDialogTitle>
              <AlertDialogDescription className="text-brand-ice/80">
                O CPF informado não é válido. Verifique os números digitados e tente novamente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction
                data-testid="cpf-invalido-ok-btn"
                className="bg-red-500/20 border border-red-400/40 text-red-100 hover:bg-red-500/30"
              >
                Entendi
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Modal — Observações / Saída do visitante (mesmo da grid) */}
        <VisitanteObsSaidaModal
          open={obsModalOpen}
          onOpenChange={setObsModalOpen}
          visitante={obsVisitante}
          onUpdated={(payload) => {
            // Atualiza inline o visitante no resultado conforme ação
            if (!payload || !resultado) return;
            const upd = payload.visitante || {};
            if (payload.tipo === "saida" && upd) {
              setResultado((prev) => prev && {
                ...prev,
                visitantes: prev.visitantes.map((it) => it.id === upd.id
                  ? { ...it, ...upd }
                  : it
                )
              });
            } else if (payload.tipo === "entrada" && upd) {
              setResultado((prev) => prev && {
                ...prev,
                visitantes: prev.visitantes.map((it) => it.id === upd.id
                  ? { ...it, ...upd }
                  : it
                )
              });
            }
          }}
        />

        {/* Modal — Histórico de Movimentos (multi-ciclo) */}
        <HistoricoMovimentosVisitanteModal
          open={movModalOpen}
          onClose={() => setMovModalOpen(false)}
          visitante={movVisitante}
          onFinalizarCiclo={handleFinalizarCiclo}
        />

        {/* Modal — Finalizar Ciclo (com campo de observação) */}
        <FinalizarCicloModal
          open={finalizarOpen}
          onClose={() => { setFinalizarOpen(false); setFinalizarVisitante(null); }}
          visitante={finalizarVisitante}
          onConfirm={(obs) => executarFinalizarCiclo(finalizarVisitante, obs)}
        />

        {/* Modal — Ajuda do Select de Modo (Lista Completa | Distintos) */}
        <ModoBuscaHelpDialog open={modoHelpOpen} onOpenChange={setModoHelpOpen} />

        {/* Modal — Registrar acesso manual de usuário fixo */}
        <RegistrarAcessoFixoModal
          open={acessoFixoOpen}
          onOpenChange={(v) => { setAcessoFixoOpen(v); if (!v) setAcessoFixoPlacaOverride(null); }}
          morador={acessoFixoMorador}
          placaBuscada={acessoFixoPlacaOverride || (tipoBusca === "placa" ? placa : "")}
          onRegistrado={handleAcessoRegistrado}
        />

        {/* Modal — Nova Agenda (aberto diretamente no formulário) */}
        <AgendasHojeModal
          open={agendaFormOpen}
          onClose={() => { setAgendaFormOpen(false); setAgendaFormInitialCpf(""); }}
          initialView="form"
          initialCpf={agendaFormInitialCpf}
        />

        {/* Mini-modal de captura de CPF para agendas sem CPF */}
        <CpfPromptDialog
          data={cpfPromptAgenda}
          onChange={setCpfPromptAgenda}
          onSubmit={salvarCpfNaAgenda}
          saving={savingCpfPromptAgenda}
        />
      </div>
    );
  }

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-login-gradient border-white/15 text-brand-ice max-w-[1400px] w-[95vw] max-h-[90vh] overflow-y-auto rounded-xl">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(78,106,156,0.18)_0%,_transparent_60%)] rounded-xl" />
        <div className="relative z-10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-brand-ice font-heading">
            <Car className="w-5 h-5 text-brand-light" />
            Pesquisar Cadastro
          </DialogTitle>
          <p className="text-brand-ice/50 text-xs">
            {tipoBusca === "placa" && "Informe a placa para localizar o proprietário"}
            {tipoBusca === "nome" && "Busque moradores, visitantes ou agendas pelo nome"}
            {tipoBusca === "cpf" && "Busque moradores, visitantes ou agendas pelo CPF"}
            {tipoBusca === "eql" && "Busque moradores, visitantes ou agendas pela EQL"}
            {tipoBusca === "empresa" && "Busque visitantes e agendas pela empresa prestadora"}
          </p>
        </DialogHeader>

        {renderContent()}
        </div>
      </DialogContent>
    </Dialog>

    {/* Popup central — CPF inválido */}
    <AlertDialog open={cpfInvalidoOpen} onOpenChange={setCpfInvalidoOpen}>
      <AlertDialogContent data-testid="cpf-invalido-dialog" className="bg-brand-dark/95 border-red-500/40 text-brand-ice">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-red-400 font-heading">CPF inválido</AlertDialogTitle>
          <AlertDialogDescription className="text-brand-ice/80">
            O CPF informado não é válido. Verifique os números digitados e tente novamente.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            data-testid="cpf-invalido-ok-btn"
            className="bg-red-500/20 border border-red-400/40 text-red-100 hover:bg-red-500/30"
          >
            Entendi
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Modal — Observações / Saída do visitante (mesmo da grid) — também acessível no modo modal */}
    <VisitanteObsSaidaModal
      open={obsModalOpen}
      onOpenChange={setObsModalOpen}
      visitante={obsVisitante}
      onUpdated={(payload) => {
        if (!payload || !resultado) return;
        const upd = payload.visitante || {};
        if (payload.tipo === "saida" && upd) {
          setResultado((prev) => prev && {
            ...prev,
            visitantes: prev.visitantes.map((it) => it.id === upd.id
              ? { ...it, ...upd }
              : it
            )
          });
        } else if (payload.tipo === "entrada" && upd) {
          setResultado((prev) => prev && {
            ...prev,
            visitantes: prev.visitantes.map((it) => it.id === upd.id
              ? { ...it, ...upd }
              : it
            )
          });
        }
      }}
    />
    {/* Modal — Histórico de Movimentos (multi-ciclo) — também acessível no modo modal */}
    <HistoricoMovimentosVisitanteModal
      open={movModalOpen}
      onClose={() => setMovModalOpen(false)}
      visitante={movVisitante}
      onFinalizarCiclo={handleFinalizarCiclo}
    />

    {/* Modal — Finalizar Ciclo (com campo de observação) — também acessível no modo modal */}
    <FinalizarCicloModal
      open={finalizarOpen}
      onClose={() => { setFinalizarOpen(false); setFinalizarVisitante(null); }}
      visitante={finalizarVisitante}
      onConfirm={(obs) => executarFinalizarCiclo(finalizarVisitante, obs)}
    />
    {/* Modal — Ajuda do Select de Modo (Lista Completa | Distintos) — modo modal também */}
    <ModoBuscaHelpDialog open={modoHelpOpen} onOpenChange={setModoHelpOpen} />

    {/* Modal — Registrar acesso manual de usuário fixo (modo modal) */}
    <RegistrarAcessoFixoModal
      open={acessoFixoOpen}
      onOpenChange={(v) => { setAcessoFixoOpen(v); if (!v) setAcessoFixoPlacaOverride(null); }}
      morador={acessoFixoMorador}
      placaBuscada={acessoFixoPlacaOverride || (tipoBusca === "placa" ? placa : "")}
      onRegistrado={handleAcessoRegistrado}
    />

    {/* Modal — Nova Agenda (aberto diretamente no formulário) */}
    <AgendasHojeModal
      open={agendaFormOpen}
      onClose={() => { setAgendaFormOpen(false); setAgendaFormInitialCpf(""); }}
      initialView="form"
      initialCpf={agendaFormInitialCpf}
    />

    {/* Mini-modal de captura de CPF para agendas sem CPF */}
    <CpfPromptDialog
      data={cpfPromptAgenda}
      onChange={setCpfPromptAgenda}
      onSubmit={salvarCpfNaAgenda}
      saving={savingCpfPromptAgenda}
    />
    </>
  );
}

function CpfPromptDialog({ data, onChange, onSubmit, saving }) {
  const fmtCpf = (v) => {
    const d = (v || "").replace(/\D/g, "").slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`;
    return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
  };
  return (
    <Dialog open={!!data} onOpenChange={(open) => { if (!open) onChange(null); }}>
      <DialogContent className="bg-brand-dark border border-amber-400/40 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-amber-200 font-heading">Informe o CPF do visitante</DialogTitle>
        </DialogHeader>
        {data && (
          <div className="space-y-3">
            <p className="text-xs text-brand-ice/60">Visitante: <b className="text-brand-ice">{data.agenda?.nome}</b></p>
            <label className="text-[10px] uppercase tracking-wider font-heading font-bold text-brand-ice/70 block">CPF *</label>
            <Input
              autoFocus
              data-testid="busca-agenda-cpf-prompt-input"
              value={data.cpfValor}
              onChange={(e) => onChange({ ...data, cpfValor: fmtCpf(e.target.value) })}
              placeholder="000.000.000-00"
              className="bg-white/10 border-brand-ice/20 text-brand-ice placeholder:text-brand-ice/40 focus:border-brand-light focus:ring-brand-light/20"
              onKeyDown={(e) => { if (e.key === "Enter") onSubmit(); }}
            />
            <p className="text-[11px] text-brand-ice/50">
              Ao gravar, o CPF será salvo no registro da agenda e o botão &quot;Confirmar&quot; ficará disponível.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => onChange(null)}
                className="px-4 py-2 text-sm text-brand-ice/70 hover:text-brand-ice transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                data-testid="busca-agenda-cpf-prompt-salvar"
                onClick={onSubmit}
                disabled={saving}
                className="px-4 py-2 text-sm font-bold bg-amber-500 hover:bg-amber-600 text-brand-dark rounded-lg transition-colors disabled:opacity-60"
              >
                {saving ? "Gravando..." : "Gravar CPF"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* =========== Modal de ajuda — Modo de Busca =========== */
function ModoBuscaHelpDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="busca-modo-help-dialog" className="bg-brand-dark/95 border-brand-ice/20 text-brand-ice max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <HelpCircle className="w-5 h-5 text-brand-light" />
            Modo de listagem dos visitantes
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 text-sm leading-relaxed">
          <div className="rounded-lg bg-white/5 border border-brand-ice/15 p-3 space-y-1">
            <p className="font-heading font-bold text-brand-ice">Lista Completa</p>
            <p className="text-brand-ice/70 text-xs">
              Exibe todas as ocorrências de visitas filtradas pelo cadastro de visitantes,
              repetindo o mesmo CPF quantas vezes ele tiver visitado.
            </p>
          </div>
          <div className="rounded-lg bg-white/5 border border-brand-ice/15 p-3 space-y-1">
            <p className="font-heading font-bold text-brand-ice">Distintos</p>
            <p className="text-brand-ice/70 text-xs">
              Agrupa visitas pelo CPF mantendo apenas a ocorrência mais recente,
              útil para responder perguntas como "quem foram os últimos motoristas
              que dirigiram este veículo?" sem mostrar todas as visitas anteriores.
            </p>
            <p className="text-amber-300 text-xs pt-1">
              <strong>Importante:</strong> registros com ciclo incompleto (sem Entrada,
              ou com Entrada e sem Saída) são <strong>sempre exibidos</strong>,
              mesmo que repitam o CPF — assim o operador pode dar baixa de cada
              um deles individualmente.
            </p>
          </div>
          <p className="text-brand-ice/50 text-[11px]">
            A preferência selecionada fica memorizada no navegador desta máquina e
            é compartilhada entre os operadores que usarem esta estação.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* =========== COLLAPSIBLE MORADORES =========== */
function SemAcessoGroup({ items, renderCard, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return null;
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        data-testid={`${testIdPrefix}-sem-acesso-toggle`}
        className="flex items-center justify-between w-full p-2 rounded-md bg-red-500/5 border border-red-500/20 hover:bg-red-500/10 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Ban className="w-3.5 h-3.5 text-red-400" />
          <span className="text-xs font-heading font-bold text-red-300 uppercase tracking-wider">Sem Acesso</span>
          <span
            data-testid={`${testIdPrefix}-sem-acesso-count`}
            className="text-[10px] bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full font-bold"
          >
            {items.length}
          </span>
        </div>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-red-400/60" /> : <ChevronDown className="w-3.5 h-3.5 text-red-400/60" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 space-y-3">
        {items.map(renderCard)}
      </CollapsibleContent>
    </Collapsible>
  );
}

// v3.12.0 — Agrupamento de EQLs por CPF em /cliente/buscar
// v3.12.5 — Chave de agrupamento passou a ser (CPF + nome normalizado):
//   familiares (esposa, filho, cônjuge) frequentemente compartilham o CPF do
//   Titular no cadastro do porteiro. Se agrupássemos só por CPF, esses
//   cadastros virariam UM card só e o operador não veria que existem várias
//   pessoas físicas distintas. A checagem por nome resolve isso — nomes
//   iguais (Titular em várias EQLs) continuam colapsando, nomes distintos
//   viram cards independentes.
// Regras confirmadas pelo usuário:
//   1) Card líder exibe a EQL PRINCIPAL preferida.
//   2) Se a Principal está BLOQUEADA e há adicional VÁLIDA → produz DOIS cards.
//   3) Se tudo bloqueado, o líder é a Principal (ou a 1ª row).
const _isRowPrincipal = (m) => !m?.is_autoriz_adicional && !m?.is_eql_adicional;

const _normNome = (s) => (s || "")
  .toString()
  .trim()
  .toUpperCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")   // remove acentos
  .replace(/\s+/g, " ");             // colapsa espaços múltiplos

const _agruparMoradoresPorCPF = (moradores) => {
  if (!Array.isArray(moradores) || moradores.length === 0) return [];
  const buckets = new Map(); // key(cpf|nome) → rows[]
  const soltos = [];
  const ordemBuckets = [];
  for (const m of moradores) {
    const cpfKey = String(m?.cpf || "").replace(/\D/g, "");
    const nomeKey = _normNome(m?.nome);
    if (!cpfKey || !nomeKey) { soltos.push(m); continue; }
    const key = `${cpfKey}|${nomeKey}`;
    if (!buckets.has(key)) { buckets.set(key, []); ordemBuckets.push(key); }
    buckets.get(key).push(m);
  }
  const cards = [];
  for (const key of ordemBuckets) {
    const rows = buckets.get(key);
    if (rows.length === 1) { cards.push({ leading: rows[0], outrasEQLs: [] }); continue; }
    const valid = rows.filter((r) => !_isMoradorBloqueado(r));
    const principal = rows.find(_isRowPrincipal) || null;
    const principalBloqueada = principal && _isMoradorBloqueado(principal);
    if (principal && !principalBloqueada) {
      const outras = rows.filter((r) => r !== principal);
      cards.push({ leading: principal, outrasEQLs: outras });
    } else if (principalBloqueada && valid.length > 0) {
      const lider = valid[0];
      const outras = rows.filter((r) => r !== principal && r !== lider);
      cards.push({ leading: principal, outrasEQLs: [] });
      cards.push({ leading: lider, outrasEQLs: outras });
    } else {
      const lider = principal || rows[0];
      const outras = rows.filter((r) => r !== lider);
      cards.push({ leading: lider, outrasEQLs: outras });
    }
  }
  for (const m of soltos) cards.push({ leading: m, outrasEQLs: [] });
  return cards;
};

function MoradoresCollapsible({ moradores, obsCountsMap = {}, open, onToggle, placaBuscada, filtroLabel = null, onIncluir, onCloseModal, onRegistrarAcesso }) {
  return (
    <Collapsible open={open} onOpenChange={onToggle}>
      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-white/5 border border-brand-ice/10 hover:bg-white/10 transition-colors">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-sky-400" />
          <span className="text-sm font-heading font-bold text-brand-ice">Usuários Fixos</span>
          <span className="text-[10px] bg-sky-500/20 text-sky-300 px-2 py-0.5 rounded-full font-bold">
            {moradores.length}
          </span>
          {filtroLabel && (
            <span
              data-testid="moradores-filtro-badge"
              className="text-[10px] bg-amber-500/20 text-amber-200 border border-amber-400/30 px-2 py-0.5 rounded-full font-semibold"
              title="Filtro aplicado no seletor 'Fixos:' — ajuste para ver todos"
            >
              filtro: {filtroLabel}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-brand-ice/50" /> : <ChevronDown className="w-4 h-4 text-brand-ice/50" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 space-y-3">
        {moradores.length === 0 ? (
          <div className="flex items-center justify-between gap-3 py-2 px-1">
            <p className="text-brand-ice/40 text-xs">Nenhum morador encontrado</p>
            <button
              type="button"
              data-testid="moradores-vazio-incluir-btn"
              onClick={onIncluir}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-light/20 border border-brand-light/30 text-brand-ice hover:bg-brand-light/30 transition-colors text-xs font-heading font-bold"
            >
              <Plus className="w-3.5 h-3.5" />
              Incluir
            </button>
          </div>
        ) : (() => {
          const grupos = _agruparMoradoresPorCPF(moradores);
          const ativos = grupos.filter((g) => !_isMoradorBloqueado(g.leading));
          const bloqueados = grupos.filter((g) => _isMoradorBloqueado(g.leading));
          const renderMorador = (g) => {
            const m = g.leading;
            return (
              <MoradorCard
                key={m.is_autoriz_adicional ? `adic-${m.autorizacao_id}` : (m.is_eql_adicional ? `eqladic-${m.eql_adicional_id}` : `princ-${m.id}`)}
                morador={m}
                outrasEQLs={g.outrasEQLs}
                obsCountsMap={obsCountsMap}
                placaBuscada={placaBuscada}
                onCloseModal={onCloseModal}
                onIncluir={onIncluir}
                onRegistrarAcesso={onRegistrarAcesso}
                obsCount={obsCountsMap[String(m.cpf || "").replace(/\D/g, "")] || { total: 0, hoje: 0 }}
              />
            );
          };
          return (
            <>
              {ativos.map(renderMorador)}
              <SemAcessoGroup items={bloqueados} renderCard={renderMorador} testIdPrefix="moradores" />
            </>
          );
        })()}
      </CollapsibleContent>
    </Collapsible>
  );
}

/* =========== CARD DO MORADOR =========== */
function MoradorCard({ morador, outrasEQLs = [], obsCountsMap = {}, placaBuscada, onCloseModal, onIncluir, onRegistrarAcesso, obsCount = { total: 0, hoje: 0 } }) {
  const navigate = useNavigate();
  const m = morador;
  const fotoUrl = m.foto_url || null;

  // Seleção de placa ativa para registro de acesso.
  // null → usa a placa principal (default). Caso contrário usa a string escolhida.
  const [selectedPlate, setSelectedPlate] = useState(null);
  // Placa efetivamente usada quando o operador clicar em "Registrar Acesso"
  const placaParaRegistro = (selectedPlate || m.placa || "").trim();

  // Montar linha de veículo que bateu com a busca
  const placaLimpa = (placaBuscada || "").toUpperCase().replace("-", "").replace(" ", "");
  const placaPrincipal = (m.placa || "").toUpperCase().replace("-", "").replace(" ", "");
  const bateuPrincipal = placaPrincipal.includes(placaLimpa);

  // Encontrar veículos adicionais que batem
  const adicionaisBatem = (m.veiculos_adicionais || []).filter((v) => {
    const p = (v.placa || "").toUpperCase().replace("-", "").replace(" ", "");
    return p.includes(placaLimpa);
  });

  // Linha PRINCIPAL sempre mostra a placa do morador (campo `placa`).
  // Quando a busca foi por placa E o hit foi em placa ADICIONAL (não bateu na
  // principal), promovemos essa(s) placa(s) para a MESMA linha da principal —
  // fica selecionável ali e não repete na lista "PLACAS ADICIONAIS" abaixo.
  const veiculoPrincipal = {
    placa: m.placa,
    cor: m.cor_veiculo,
    descricao: m.descricao_veiculo,
    vaga: m.vaga_marcada,
  };
  const buscaPorPlaca = !!placaBuscada;
  const adicionaisNaLinhaPrincipal = (buscaPorPlaca && !bateuPrincipal && adicionaisBatem.length > 0)
    ? adicionaisBatem
    : [];
  const placasPromovidasSet = new Set(adicionaisNaLinhaPrincipal.map((v) => v.placa));
  // Lista de adicionais restantes que vão para o collapsible.
  const veiculosExtras = (m.veiculos_adicionais || []).filter((v) => !placasPromovidasSet.has(v.placa));
  const adicionalBateuSet = new Set(adicionaisBatem.map((v) => v.placa));

  // Pré-seleciona a placa que o operador BUSCOU. Se a busca por placa bateu numa
  // placa ADICIONAL (não na principal), é ESSA que deve vir selecionada para o
  // registro de acesso — não a principal do cadastro. Evita dar entrada no carro
  // errado quando o veículo na portaria é um adicional.
  useEffect(() => {
    if (buscaPorPlaca && !bateuPrincipal && adicionaisBatem.length > 0) {
      setSelectedPlate(adicionaisBatem[0].placa);
    } else {
      setSelectedPlate(null); // principal
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m.id, placaLimpa]);

  // Collapsible "PLACAS ADICIONAIS" — abre automaticamente se a busca por placa
  // bateu num adicional que continua no collapsible (fallback raro: dois hits,
  // um promovido e outros no collapsible).
  const [adicionaisOpen, setAdicionaisOpen] = useState(false);
  // v3.12.0 — Collapsible de EQLs adicionais agrupadas (por CPF).
  const [outrasEQLsOpen, setOutrasEQLsOpen] = useState(false);

  // Regras de bloqueio (ordem de prioridade — do mais específico ao mais genérico).
  // O check de `ativo === false` fica por ÚLTIMO: se um cadastro está inativo por
  // causa de motivo de bloqueio ou validade vencida, queremos exibir a razão
  // específica antes do rótulo genérico "Inativo". A checagem é feita client-side
  // com o horário local do navegador — que na prática corresponde ao fuso
  // configurado do cliente. O backend também impõe a inativação via timer e no
  // PUT/POST de moradores.
  const bloqueio = (() => {
    // v3.11.0 — Se o CPF tem cobertura em outra EQL (backend disse
    // principal_sem_autorizador_warning=true e bloqueio_efetivo=false),
    // a linha NÃO é bloqueada. O warning aparece em outro lugar do card.
    if (m.bloqueio_efetivo === false) return null;
    // v1.9.0 — Prioridade MÁXIMA: autorização adicional (Vencida > Inativa).
    // Uma linha "virtual" bloqueada nunca deve permitir Registrar Acesso.
    const asit = m.autorizacao_situacao;
    if (asit && asit.tipo && asit.tipo !== "ok") {
      return {
        label: asit.label || "Autorização Bloqueada",
        motivo: asit.motivo || asit.label || "Autorização adicional não disponível",
      };
    }
    const mb = (m.motivo_bloqueio || "").trim();
    if (mb) return { label: `Bloqueado por ${mb}`, motivo: `Motivo: ${mb}` };
    const vf = m.validade_final;
    if (vf) {
      try {
        const s = String(vf);
        const iso = s.length === 10 ? `${s}T23:59:59` : (s.length === 16 ? `${s}:00` : s);
        const d = new Date(iso);
        if (!isNaN(d.getTime()) && new Date() > d) {
          return { label: "Vencido", motivo: `Validade encerrada em ${d.toLocaleString("pt-BR")}` };
        }
      } catch { /* ignora */ }
    }
    if (m.ativo === false) return { label: "Inativo", motivo: "Cadastro inativo" };
    // Fase 2 — Autorizador: se o cadastro (tipo != 16) não possui autorizador
    // ATIVO em nenhuma EQL, bloqueia com o rótulo priorizado retornado pelo
    // backend (Vencido > Bloqueado > Inativo > Sem Autorizador).
    const sit = m.autorizador_situacao;
    if (sit && sit.tipo && sit.tipo !== "ok") {
      return {
        label: sit.label || "Sem Autorizador",
        motivo: sit.motivo || sit.label || "Autorizador não disponível",
      };
    }
    return null;
  })();

  // Linha 2: Local de Acesso e Número, Quadra, Lote e Gleba (se existir)
  const temLinha2 = m.numero_casa || m.quadra || m.lote;
  let linha2Parts = [];
  if (m.local_acesso_descricao) linha2Parts.push(m.local_acesso_descricao);
  if (m.numero_casa) linha2Parts.push(m.numero_casa);
  if (m.quadra) linha2Parts.push(`Qd ${m.quadra}`);
  if (m.lote) linha2Parts.push(`Lt ${m.lote}`);
  if (m.gleba) linha2Parts.push(`Gleba ${m.gleba}`);

  // Linha 3: Local de Acesso, Andar, Unidade, Bloco e Prédio (se existir Andar)
  const temLinha3 = m.andar;
  let linha3Parts = [];
  if (temLinha3) {
    if (m.local_acesso_descricao) linha3Parts.push(m.local_acesso_descricao);
    if (m.andar) linha3Parts.push(`And. ${m.andar}`);
    if (m.sala) linha3Parts.push(`Un. ${m.sala}`);
    if (m.bloco_descricao) linha3Parts.push(m.bloco_descricao);
    if (m.nome_predio) linha3Parts.push(m.nome_predio);
  }

  const temCelular = m.celular && m.celular.trim();

  return (
    <div data-testid={`morador-card-${m.id}`} className="rounded-lg bg-white/5 border border-brand-ice/10 p-3">
      <div className="flex gap-3">
        {/* Foto */}
        <div className="flex-shrink-0 w-16 h-20 rounded-lg overflow-hidden bg-brand-dark/60 border border-brand-ice/10 flex items-center justify-center">
          {fotoUrl ? (
            <img src={fotoUrl} alt={m.nome} className="w-full h-full object-cover" />
          ) : (
            <User className="w-8 h-8 text-brand-ice/20" />
          )}
        </div>

        {/* Dados */}
        <div className="flex-1 min-w-0">
          {/* Linha 1: Departamento, EQL, Nome, Toggle Vaga */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {m.tipo_cartao_descricao && (
                  <span className="text-[11px] font-heading font-bold tracking-wider text-sky-300">
                    {String(m.tipo_cartao_descricao).toUpperCase()}
                  </span>
                )}
                {m.is_autoriz_adicional && (
                  <span
                    data-testid={`autoriz-adicional-chip-${m.autorizacao_id || m.id}`}
                    className="text-[9px] font-heading font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-md bg-indigo-500/20 border border-indigo-400/40 text-indigo-200"
                    title="Este acesso vem de uma Autorização Adicional (multi-EQL), não do cadastro principal."
                  >
                    Autorização Adicional
                  </span>
                )}
                {m.is_eql_adicional && (
                  <span
                    data-testid={`eql-adicional-chip-${m.eql_adicional_id || m.id}`}
                    className="text-[9px] font-heading font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-md bg-emerald-500/20 border border-emerald-400/40 text-emerald-200"
                    title="Morador proprietário de múltiplos lotes — este é um lote adicional."
                  >
                    EQL Adicional
                  </span>
                )}
                {m.eql && (
                  <span className="flex items-center gap-1">
                    <span className="text-sm font-bold text-brand-ice">{m.eql}</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(m.eql);
                        const t = document.createElement('div');
                        t.textContent = `EQL ${m.eql} copiada!`;
                        t.className = 'fixed top-16 right-4 z-[9999] bg-green-600 text-white text-xs px-3 py-2 rounded-lg shadow-lg';
                        document.body.appendChild(t);
                        setTimeout(() => t.remove(), 1500);
                      }}
                      className="p-0.5 rounded text-brand-ice/30 hover:text-brand-ice transition-colors"
                      title={`Copiar EQL ${m.eql}`}
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
              <p className="text-sm text-brand-ice font-medium truncate flex items-center gap-2">
                <span className="truncate">{m.nome}</span>
                {/* v3.10.0 (Fase 4) — Badge de contadores de observações-CPF.
                    Âmbar quando há obs registradas HOJE (chamativo — porteiro
                    precisa saber que teve anotação recente sobre este CPF).
                    Cinza discreto quando só existe histórico de dias
                    anteriores. Escondido quando total=0. */}
                {obsCount.hoje > 0 ? (
                  <span
                    data-testid={`morador-obs-badge-hoje-${m.id}`}
                    className="inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border bg-amber-500/25 text-amber-100 border-amber-400/50"
                    title={`Este CPF tem ${obsCount.hoje} observação(ões) registrada(s) HOJE. Clique em "Registrar Acesso" para conferir.`}
                  >
                    📝 OBS HOJE ({obsCount.hoje})
                  </span>
                ) : obsCount.total > 0 ? (
                  <span
                    data-testid={`morador-obs-badge-hist-${m.id}`}
                    className="inline-flex items-center gap-1 text-[10px] font-heading font-medium uppercase tracking-wider px-1.5 py-0.5 rounded-md border bg-white/5 text-brand-ice/60 border-brand-ice/20"
                    title={`Este CPF tem ${obsCount.total} observação(ões) no histórico (nada registrado hoje).`}
                  >
                    📝 OBS ({obsCount.total})
                  </span>
                ) : null}
                {m.cpf_eql_stats && m.cpf_eql_stats.total > 1 && (() => {
                  const { total, habilitadas } = m.cpf_eql_stats;
                  const ok = habilitadas === total;
                  const parcial = habilitadas > 0 && habilitadas < total;
                  const zero = habilitadas === 0;
                  const cor = ok
                    ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/40"
                    : parcial
                      ? "bg-amber-500/20 text-amber-200 border-amber-400/40"
                      : "bg-red-500/25 text-red-200 border-red-400/40";
                  return (
                    <span
                      data-testid={`cpf-eql-stats-${m.id}${m.is_autoriz_adicional ? "-adic-" + (m.autorizacao_id || "") : ""}`}
                      className={`inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${cor}`}
                      title={`Este CPF autoriza em ${total} EQL${total !== 1 ? "s" : ""}, sendo ${habilitadas} habilitada${habilitadas !== 1 ? "s" : ""} agora`}
                    >
                      {habilitadas}/{total} EQLs {zero ? "BLOQ" : "OK"}
                    </span>
                  );
                })()}
              </p>
              {/* Tags de Setor + Funções (principal + adicionais) — múltiplas funções por CPF */}
              {(m.departamento_descricao || m.funcao_descricao || (Array.isArray(m.funcoes_adicionais_descricoes) && m.funcoes_adicionais_descricoes.length > 0) || m.empresa_nome) && (
                <div className="flex items-center gap-1 flex-wrap mt-0.5">
                  {m.departamento_descricao && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-amber-500/15 border border-amber-400/30 text-amber-200 font-medium">
                      {m.departamento_descricao}
                    </span>
                  )}
                  {m.funcao_descricao && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-brand-light/15 border border-brand-light/30 text-brand-light font-medium">
                      {m.funcao_descricao}
                    </span>
                  )}
                  {Array.isArray(m.funcoes_adicionais_descricoes) && m.funcoes_adicionais_descricoes.map((fd, i) => (
                    <span key={`${m.id}-bf-${i}`} className="text-[10px] px-1.5 py-0.5 rounded-md bg-sky-500/15 border border-sky-400/30 text-sky-200 font-medium">
                      +{fd}
                    </span>
                  ))}
                  {/* Empresa (empresa_id → cliente_prestadores.nome) — útil na busca por Empresa */}
                  {m.empresa_nome && (
                    <span
                      data-testid={`morador-empresa-${m.id}`}
                      title="Empresa do vínculo (Empresa)"
                      className="text-[10px] px-1.5 py-0.5 rounded-md bg-fuchsia-500/15 border border-fuchsia-400/30 text-fuchsia-200 font-medium"
                    >
                      🏢 {m.empresa_nome}
                    </span>
                  )}
                </div>
              )}
              {/* Parentesco isolado (Setor e Função já estão nos badges acima) */}
              {m.parentesco_descricao && (
                <p className="text-xs text-brand-ice/40 truncate">{m.parentesco_descricao}</p>
              )}
              {/* Validade — para linha virtual (autorização adicional), mostra
                  a validade da própria autorização (não do cadastro principal). */}
              {(() => {
                const vi = m.is_autoriz_adicional ? m.autorizacao_validade_inicial : m.validade_inicial;
                const vf = m.is_autoriz_adicional ? m.autorizacao_validade_final : m.validade_final;
                if (!vi && !vf) return null;
                const fmtDate = (d) => { if (!d) return ""; const dt = d.split("T")[0]; const [y,mo,dy] = dt.split("-"); return `${dy}/${mo}/${y}`; };
                const ini = fmtDate(vi);
                const fim = fmtDate(vf);
                let diasRestantes = null;
                let status = "valido";
                if (vf) {
                  const hoje = new Date(); hoje.setHours(0,0,0,0);
                  const [y2, mo2, d2] = vf.split("T")[0].split("-");
                  const valFim = new Date(Number(y2), Number(mo2) - 1, Number(d2));
                  valFim.setHours(0,0,0,0);
                  diasRestantes = Math.round((valFim - hoje) / (1000*60*60*24));
                  if (diasRestantes < 0) status = "vencido";
                  else if (diasRestantes === 0) status = "hoje";
                  else if (diasRestantes < 30) status = "vencendo";
                }
                const cor = { valido: "text-green-400", hoje: "text-amber-400", vencendo: "text-amber-400", vencido: "text-red-400" }[status];
                let sufixo = "";
                if (diasRestantes !== null) {
                  if (status === "vencido") {
                    const abs = Math.abs(diasRestantes);
                    sufixo = ` — Vencido há ${abs} dia${abs !== 1 ? "s" : ""}`;
                  } else if (status === "hoje") {
                    sufixo = " — Vence hoje";
                  } else if (status === "vencendo") {
                    sufixo = ` — Vencendo em ${diasRestantes} dia${diasRestantes !== 1 ? "s" : ""}`;
                  }
                }
                return (
                  <div className={`flex items-center gap-1 mt-0.5 ${cor} ${status === "vencido" ? "blink-expired" : ""}`}>
                    <Calendar className="w-3 h-3" />
                    <span className="text-xs">
                      {ini && fim ? `${ini} até ${fim}` : fim ? `até ${fim}` : ini}
                      {sufixo}
                    </span>
                  </div>
                );
              })()}
            </div>
            {/* Toggle Vaga + Editar + Incluir + Registrar Acesso (abaixo, alinhado à direita) */}
            <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1" title={m.pode_utilizar_vaga ? "Pode usar vaga" : "Sem vaga"}>
                  <div className={`w-8 h-4 rounded-full flex items-center px-0.5 transition-colors ${m.pode_utilizar_vaga ? 'bg-green-500/60' : 'bg-brand-ice/10'}`}>
                    <div className={`w-3 h-3 rounded-full transition-transform ${m.pode_utilizar_vaga ? 'translate-x-4 bg-white' : 'translate-x-0 bg-brand-ice/30'}`} />
                  </div>
                  <span className="text-[9px] text-brand-ice/40">Vaga</span>
                </div>
                <button
                  onClick={() => {
                    onCloseModal();
                    navigate(`/cliente/moradores/${m.id}`);
                  }}
                  className="p-1.5 rounded-lg text-brand-ice/40 hover:text-brand-light hover:bg-white/10 transition-colors"
                  title="Editar Cadastro"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                {onIncluir && (
                  <button
                    data-testid={`morador-incluir-novo-btn-${m.id}`}
                    onClick={onIncluir}
                    className="p-1.5 rounded-lg text-brand-ice/40 hover:text-green-400 hover:bg-white/10 transition-colors"
                    title="Incluir novo usuário fixo"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {bloqueio ? (
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  <span
                    data-testid={`morador-bloqueio-badge-${m.id}`}
                    title={bloqueio.motivo}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/25 border border-red-400/60 text-red-200 text-[11px] font-heading font-bold uppercase tracking-wider max-w-[280px] truncate"
                  >
                    ⛔ {bloqueio.label}
                  </span>
                  {/* Atalho para o cadastro do autorizador quando o bloqueio veio do autorizador (Fase 2). */}
                  {m.autorizador_situacao && m.autorizador_situacao.autorizador_id && (
                    <button
                      type="button"
                      data-testid={`morador-ir-autorizador-btn-${m.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCloseModal && onCloseModal();
                        navigate(`/cliente/moradores/${m.autorizador_situacao.autorizador_id}`);
                      }}
                      title={`Abrir cadastro do autorizador: ${m.autorizador_situacao.autorizador_nome || ""} (EQL ${m.autorizador_situacao.eql_autorizador || "?"})`}
                      className="inline-flex items-center gap-1 px-1.5 py-1 rounded-md text-[10px] font-heading font-medium text-brand-ice/60 hover:text-brand-light hover:bg-white/5 transition-colors underline-offset-2 hover:underline"
                    >
                      → Ir para autorizador
                    </button>
                  )}
                </div>
              ) : onRegistrarAcesso && (
                <button
                  data-testid={`morador-registrar-acesso-btn-${m.id}`}
                  onClick={() => onRegistrarAcesso(m, placaParaRegistro)}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-sky-500/15 hover:bg-sky-500/25 border border-sky-400/30 text-sky-300 hover:text-sky-200 transition-colors text-[11px] font-heading font-bold"
                  title={placaParaRegistro ? `Registrar acesso com placa ${placaParaRegistro}` : "Registrar acesso manual (Entrada/Saída)"}
                >
                  <Radio className="w-3 h-3" />
                  Registrar Acesso
                </button>
              )}
            </div>
          </div>

          {/* Linha 2: Local, Número, Quadra, Lote, Gleba */}
          {temLinha2 && (
            <p className="text-sm text-brand-ice/50 mt-0.5 truncate">{linha2Parts.join(", ")}</p>
          )}

          {/* Linha 3: Andar, Unidade, Bloco, Prédio */}
          {temLinha3 && (
            <p className="text-sm text-brand-ice/50 truncate">{linha3Parts.join(", ")}</p>
          )}

          {/* Linha 5: WhatsApp */}
          {temCelular && (
            <button
              onClick={() => openWhatsAppPopup(m.ddi_celular, m.celular)}
              className="flex items-center gap-1.5 mt-1 text-green-400 hover:text-green-300 transition-colors"
              title="Chamar pelo WhatsApp"
            >
              <Phone className="w-3.5 h-3.5" />
              <span className="text-[15px] font-mono">{m.ddi_celular || "+55"} {m.celular}</span>
            </button>
          )}

          {/* Linha 6: Placa principal (+ placas adicionais promovidas se a busca por placa bateu num adicional) */}
          <div className="flex items-center gap-2 mt-1 flex-wrap"
               data-testid={`morador-veiculo-principal-${m.id}`}>
            {(() => {
              // Default: principal está "selecionada" quando selectedPlate é null,
              // OU quando o operador clicou explicitamente nela.
              const isSelected = selectedPlate == null || selectedPlate === veiculoPrincipal.placa;
              return (
                <button
                  type="button"
                  data-testid={`morador-placa-principal-btn-${m.id}`}
                  onClick={() => setSelectedPlate(veiculoPrincipal.placa || null)}
                  title={isSelected ? "Placa selecionada para o registro" : "Clique para selecionar esta placa no registro de acesso"}
                  aria-pressed={isSelected}
                  className={`font-mono font-bold transition-all rounded ${
                    isSelected
                      ? "text-[17px] text-amber-300 bg-amber-500/15 border border-amber-400/70 ring-1 ring-amber-400/40 px-2 py-0.5 shadow-sm shadow-amber-500/20"
                      : `text-[15px] text-amber-400 px-1 border border-transparent hover:bg-white/5 hover:border-amber-400/30 ${bateuPrincipal ? "ring-1 ring-amber-400/60" : ""}`
                  }`}
                >{veiculoPrincipal.placa}</button>
              );
            })()}
            {veiculoPrincipal.cor && <span className="text-sm text-brand-ice/50">{veiculoPrincipal.cor}</span>}
            {veiculoPrincipal.descricao && <span className="text-sm text-brand-ice/50">{veiculoPrincipal.descricao}</span>}
            {veiculoPrincipal.vaga && <span className="text-sm text-brand-ice/60 font-mono">Vg: {veiculoPrincipal.vaga}</span>}
            {/* Placas adicionais promovidas — hit da busca em placa adicional.
                Ficam ao lado da principal e são selecionáveis diretamente. */}
            {adicionaisNaLinhaPrincipal.map((v, idx) => {
              const isSelected = selectedPlate === v.placa;
              return (
                <span key={v.id || `promo-${v.placa}-${idx}`} className="flex items-center gap-2">
                  <span className="text-brand-ice/30 text-xs">•</span>
                  <button
                    type="button"
                    data-testid={`morador-placa-adicional-promovida-btn-${m.id}-${idx}`}
                    onClick={() => setSelectedPlate(v.placa)}
                    title={isSelected ? "Placa selecionada para o registro" : "Placa adicional (bateu na busca) — clique para selecioná-la"}
                    aria-pressed={isSelected}
                    className={`font-mono font-bold transition-all rounded ${
                      isSelected
                        ? "text-[17px] text-amber-300 bg-amber-500/15 border border-amber-400/70 ring-1 ring-amber-400/40 px-2 py-0.5 shadow-sm shadow-amber-500/20"
                        : "text-[15px] text-amber-400 px-1 border border-transparent hover:bg-white/5 hover:border-amber-400/30 ring-1 ring-amber-400/60"
                    }`}
                  >{v.placa}</button>
                  {v.cor && <span className="text-sm text-brand-ice/50">{v.cor}</span>}
                  {v.descricao && <span className="text-sm text-brand-ice/50">{v.descricao}</span>}
                  {v.vaga && <span className="text-sm text-brand-ice/60 font-mono">Vg: {v.vaga}</span>}
                </span>
              );
            })}
          </div>

          {/* Linha 7: PLACAS ADICIONAIS (cliente_veiculos_cpf por CPF do morador) — collapsible */}
          {veiculosExtras.length > 0 && (
            <div className="mt-1.5 pl-2 border-l-2 border-amber-400/20"
                 data-testid={`morador-veiculos-adicionais-${m.id}`}>
              <button
                type="button"
                data-testid={`morador-adicionais-toggle-${m.id}`}
                onClick={() => setAdicionaisOpen((v) => !v)}
                aria-expanded={adicionaisOpen}
                className="w-full flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-300/60 hover:text-amber-300 font-heading font-bold mb-0.5 transition-colors"
              >
                <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${adicionaisOpen ? "rotate-0" : "-rotate-90"}`} />
                <span>Placas Adicionais</span>
                <span className="text-amber-300/40 normal-case tracking-normal">({veiculosExtras.length})</span>
              </button>
              {adicionaisOpen && (
                <div className="space-y-0.5">
                  {veiculosExtras.map((v, idx) => {
                    const bateu = adicionalBateuSet.has(v.placa);
                    const isSelected = selectedPlate === v.placa;
                    return (
                      <div key={v.id || `${v.placa}-${idx}`}
                           data-testid={`morador-veiculo-adicional-${m.id}-${idx}`}
                           className="flex items-center gap-2 flex-wrap text-sm">
                        <button
                          type="button"
                          data-testid={`morador-placa-adicional-btn-${m.id}-${idx}`}
                          onClick={() => setSelectedPlate(v.placa)}
                          title={isSelected ? "Placa selecionada para o registro" : "Clique para selecionar esta placa no registro de acesso"}
                          aria-pressed={isSelected}
                          className={`font-mono font-bold transition-all rounded ${
                            isSelected
                              ? "text-[17px] text-amber-300 bg-amber-500/15 border border-amber-400/70 ring-1 ring-amber-400/40 px-2 py-0.5 shadow-sm shadow-amber-500/20"
                              : `text-[15px] text-amber-400 px-1 border border-transparent hover:bg-white/5 hover:border-amber-400/30 ${bateu ? "ring-1 ring-amber-400/60" : ""}`
                          }`}
                        >{v.placa}</button>
                        {v.cor && <span className="text-sm text-brand-ice/50">{v.cor}</span>}
                        {v.descricao && <span className="text-sm text-brand-ice/50">{v.descricao}</span>}
                        {v.vaga && <span className="text-sm text-brand-ice/60 font-mono">Vg: {v.vaga}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* v3.12.0 — EQLs ADICIONAIS (mesmo CPF em múltiplos vínculos) */}
          {outrasEQLs.length > 0 && (
            <OutrasEQLsCollapsible
              open={outrasEQLsOpen}
              onToggle={() => setOutrasEQLsOpen((v) => !v)}
              parentId={m.id}
              itens={outrasEQLs}
              obsCountsMap={obsCountsMap}
              onRegistrarAcesso={onRegistrarAcesso}
              onCloseModal={onCloseModal}
            />
          )}
        </div>
      </div>
    </div>
  );
}


/* =========== EQLs ADICIONAIS DO MESMO CPF (collapsible interno) ===========
 * v3.12.0 — Quando um CPF tem múltiplos vínculos (Principal + Autorizações
 * Adicionais + EQLs Adicionais), o card líder resume o vínculo dominante e
 * este componente lista os demais em formato compacto, com badge "+N EQLs".
 */
function OutrasEQLsCollapsible({ open, onToggle, parentId, itens, obsCountsMap = {}, onRegistrarAcesso, onCloseModal }) {
  const navigate = useNavigate();
  const count = itens.length;
  return (
    <div className="mt-2 pl-2 border-l-2 border-indigo-400/25"
         data-testid={`morador-outras-eqls-${parentId}`}>
      <button
        type="button"
        data-testid={`morador-outras-eqls-toggle-${parentId}`}
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-indigo-300/70 hover:text-indigo-200 font-heading font-bold mb-1 transition-colors"
      >
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? "rotate-0" : "-rotate-90"}`} />
        <span>Outras Autorizações do CPF</span>
        <span
          data-testid={`morador-outras-eqls-badge-${parentId}`}
          className="text-[10px] px-1.5 py-0.5 rounded-md bg-indigo-500/25 border border-indigo-400/50 text-indigo-100 font-heading font-bold normal-case tracking-normal"
        >+{count} EQL{count !== 1 ? "s" : ""}</span>
      </button>
      {open && (
        <div className="space-y-1.5">
          {itens.map((o, idx) => {
            const bloqueada = _isMoradorBloqueado(o);
            const chip = o.is_autoriz_adicional
              ? { label: "Autorização Adicional", cls: "bg-indigo-500/20 border-indigo-400/40 text-indigo-200" }
              : o.is_eql_adicional
                ? { label: "EQL Adicional", cls: "bg-emerald-500/20 border-emerald-400/40 text-emerald-200" }
                : { label: "Principal", cls: "bg-sky-500/20 border-sky-400/40 text-sky-200" };
            const localParts = [];
            if (o.local_acesso_descricao) localParts.push(o.local_acesso_descricao);
            if (o.numero_casa) localParts.push(o.numero_casa);
            if (o.quadra) localParts.push(`Qd ${o.quadra}`);
            if (o.lote) localParts.push(`Lt ${o.lote}`);
            if (o.andar) localParts.push(`And ${o.andar}`);
            if (o.sala) localParts.push(`Un ${o.sala}`);
            const vi = o.is_autoriz_adicional ? o.autorizacao_validade_inicial : o.validade_inicial;
            const vf = o.is_autoriz_adicional ? o.autorizacao_validade_final : o.validade_final;
            const rowKey = o.is_autoriz_adicional ? `adic-${o.autorizacao_id}` : (o.is_eql_adicional ? `eqladic-${o.eql_adicional_id}` : `princ-${o.id}`);
            return (
              <div
                key={rowKey}
                data-testid={`morador-outra-eql-row-${parentId}-${idx}`}
                className={`flex items-center gap-2 flex-wrap p-2 rounded-md border ${bloqueada ? "bg-red-500/10 border-red-400/25" : "bg-white/[0.03] border-brand-ice/10"}`}
              >
                <span
                  className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md border font-heading font-bold uppercase tracking-wider whitespace-nowrap ${chip.cls}`}
                  title={chip.label}
                >
                  {chip.label}
                </span>
                {o.eql && (
                  <span className="text-sm font-bold text-brand-ice whitespace-nowrap">{o.eql}</span>
                )}
                {(o.departamento_descricao || o.funcao_descricao) && (
                  <span className="text-[11px] text-brand-ice/70 truncate">
                    {[o.departamento_descricao, o.funcao_descricao].filter(Boolean).join(" · ")}
                  </span>
                )}
                {localParts.length > 0 && (
                  <span className="text-[11px] text-brand-ice/50 truncate">
                    {localParts.join(", ")}
                  </span>
                )}
                {(vi || vf) && (
                  <span className="text-[11px] text-brand-ice/60 whitespace-nowrap">
                    <Calendar className="inline w-3 h-3 mr-0.5 -mt-0.5" />
                    {fmtDateOnly(vi)}{vf ? ` → ${fmtDateOnly(vf)}` : ""}
                  </span>
                )}
                <span className="ml-auto flex items-center gap-1.5">
                  {bloqueada ? (
                    <span
                      data-testid={`morador-outra-eql-bloq-${parentId}-${idx}`}
                      className="text-[10px] px-1.5 py-0.5 rounded-md bg-red-500/25 border border-red-400/60 text-red-200 font-heading font-bold uppercase tracking-wider"
                      title={o.motivo_bloqueio || o.autorizacao_situacao?.label || o.autorizador_situacao?.label || "Bloqueado"}
                    >⛔ Bloq</span>
                  ) : onRegistrarAcesso ? (
                    <button
                      type="button"
                      data-testid={`morador-outra-eql-registrar-btn-${parentId}-${idx}`}
                      onClick={() => onRegistrarAcesso(o, (o.placa || "").trim())}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-sky-500/15 hover:bg-sky-500/25 border border-sky-400/30 text-sky-300 hover:text-sky-200 transition-colors text-[10px] font-heading font-bold uppercase tracking-wider"
                      title="Registrar acesso por esta autorização"
                    >
                      <Radio className="w-3 h-3" />
                      Registrar
                    </button>
                  ) : null}
                  <button
                    type="button"
                    data-testid={`morador-outra-eql-editar-btn-${parentId}-${idx}`}
                    onClick={() => { onCloseModal && onCloseModal(); navigate(`/cliente/moradores/${o.id}`); }}
                    className="p-1 rounded-md text-brand-ice/40 hover:text-brand-light hover:bg-white/10 transition-colors"
                    title="Abrir cadastro"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
const fmtDateOnly = (d) => {
  if (!d) return "";
  const dt = String(d).split("T")[0];
  const [y, mo, dy] = dt.split("-");
  if (!y) return "";
  return `${dy}/${mo}/${y}`;
};
const fmtDateTimeShort = (iso) => {
  if (!iso) return "";
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return "";
    const dia = String(dt.getDate()).padStart(2, "0");
    const mes = String(dt.getMonth() + 1).padStart(2, "0");
    const ano = String(dt.getFullYear()).slice(-2);
    const hora = String(dt.getHours()).padStart(2, "0");
    const min = String(dt.getMinutes()).padStart(2, "0");
    return `${dia}/${mes}/${ano} ${hora}:${min}`;
  } catch {
    return "";
  }
};

/* Bloco de Validade — mesma fonte/cores do MoradorCard.
 * v3.8.13 — Prop `considerarHorario` (default false): quando true e o
 * status calculado é "hoje", verifica se o HORÁRIO do `final` já passou —
 * nesse caso o status vira "horario_vencido" e o texto exibe
 * "Horário Vencido" (mesmo visual de "vencido": vermelho + piscando).
 * Usado no AgendaCard para refletir vencimento por horário em agendas
 * marcadas para o dia corrente.
 */
function ValidadeLine({ inicial, final, considerarHorario = false }) {
  if (!inicial && !final) return null;
  const ini = fmtDateOnly(inicial);
  const fim = fmtDateOnly(final);
  let diasRestantes = null;
  let status = "valido"; // "valido" | "hoje" | "vencendo" | "vencido" | "horario_vencido"
  if (final) {
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
    // Parse manual evitando UTC para preservar horário local
    const [yStr, moStr, dStr] = String(final).split("T")[0].split("-");
    const valFim = new Date(Number(yStr), Number(moStr) - 1, Number(dStr));
    valFim.setHours(0, 0, 0, 0);
    diasRestantes = Math.round((valFim - hoje) / (1000 * 60 * 60 * 24));
    if (diasRestantes < 0) status = "vencido";
    else if (diasRestantes === 0) status = "hoje";
    else if (diasRestantes < 30) status = "vencendo";

    // v3.8.13 — Se `considerarHorario` e vencimento é hoje, checa a hora exata.
    // Aceita "YYYY-MM-DDTHH:MM:SS" (com ou sem timezone). Se não houver hora
    // no string (só data), assume 23:59:59 como fim do dia (não vence antes).
    if (considerarHorario && status === "hoje") {
      const rawFinal = String(final);
      const tPart = rawFinal.includes("T") ? rawFinal.split("T")[1] : null;
      let hFim = 23, mFim = 59, sFim = 59;
      if (tPart) {
        const clean = tPart.replace("Z", "").split(/[+-]/)[0];
        const [hh, mm, ss] = clean.split(":");
        hFim = Number(hh) || 0;
        mFim = Number(mm) || 0;
        sFim = Number((ss || "0").split(".")[0]) || 0;
      }
      const agora = new Date();
      const finalTs = new Date(
        Number(yStr), Number(moStr) - 1, Number(dStr), hFim, mFim, sFim,
      );
      if (agora.getTime() > finalTs.getTime()) {
        status = "horario_vencido";
      }
    }
  }
  const corMap = {
    valido: "text-green-400",
    hoje: "text-amber-400",
    vencendo: "text-amber-400",
    vencido: "text-red-400",
    horario_vencido: "text-red-400",
  };
  let sufixo = "";
  if (diasRestantes !== null) {
    if (status === "vencido") {
      const abs = Math.abs(diasRestantes);
      sufixo = ` — Vencido há ${abs} dia${abs !== 1 ? "s" : ""}`;
    } else if (status === "horario_vencido") {
      sufixo = " — Horário Vencido";
    } else if (status === "hoje") {
      sufixo = " — Vence hoje";
    } else if (status === "vencendo") {
      sufixo = ` — Vencendo em ${diasRestantes} dia${diasRestantes !== 1 ? "s" : ""}`;
    }
  }
  const blink = status === "vencido" || status === "horario_vencido";
  return (
    <div className={`flex items-center gap-1 mt-0.5 ${corMap[status]} ${blink ? "blink-expired" : ""}`}>
      <Calendar className="w-3 h-3" />
      <span className="text-xs">
        {ini && fim ? `${ini} até ${fim}` : fim ? `até ${fim}` : ini}
        {sufixo}
      </span>
    </div>
  );
}

/* Bloco de WhatsApp — mesmo estilo */
function WhatsAppLine({ ddi, celular }) {
  if (!celular || !String(celular).trim()) return null;
  return (
    <button
      onClick={() => openWhatsAppPopup(ddi, celular)}
      className="flex items-center gap-1.5 mt-1 text-green-400 hover:text-green-300 transition-colors"
      title="Chamar pelo WhatsApp"
    >
      <Phone className="w-3.5 h-3.5" />
      <span className="text-[15px] font-mono">{ddi || "+55"} {celular}</span>
    </button>
  );
}

/* EQL com botão copiar — mesmo estilo do MoradorCard */
function EQLLine({ eql }) {
  if (!eql) return null;
  return (
    <span className="flex items-center gap-1">
      <span className="text-sm font-bold text-brand-ice">{eql}</span>
      <button
        onClick={() => {
          navigator.clipboard.writeText(eql);
          const t = document.createElement("div");
          t.textContent = `EQL ${eql} copiada!`;
          t.className = "fixed top-16 right-4 z-[9999] bg-green-600 text-white text-xs px-3 py-2 rounded-lg shadow-lg";
          document.body.appendChild(t);
          setTimeout(() => t.remove(), 1500);
        }}
        className="p-0.5 rounded text-brand-ice/30 hover:text-brand-ice transition-colors"
        title={`Copiar EQL ${eql}`}
      >
        <Copy className="w-3 h-3" />
      </button>
    </span>
  );
}

/* =========== TIPOS DE CADASTRO (visitante/agenda) =========== */
// Cada tipo tem: label, cor de texto + classes de fundo/borda no padrão dos botões de ação.
// O `badgeClass` monta um mini-card com bold/tracking igual aos botões Incluir/Entrada/Saída.
const TIPO_CADASTRO_MAP = {
  0: { label: "VISITANTE",    color: "text-pink-300",    badgeClass: "bg-pink-500/15 border-pink-400/30 text-pink-200" },
  1: { label: "PRESTADOR",    color: "text-blue-300",    badgeClass: "bg-blue-500/15 border-blue-400/30 text-blue-200" },
  2: { label: "MORADOR",      color: "text-sky-300",     badgeClass: "bg-sky-500/15 border-sky-400/30 text-sky-200" },
  3: { label: "FUNCIONÁRIO",  color: "text-emerald-300", badgeClass: "bg-emerald-500/15 border-emerald-400/30 text-emerald-200" },
  4: { label: "PROPRIETÁRIO", color: "text-purple-300",  badgeClass: "bg-purple-500/15 border-purple-400/30 text-purple-200" },
  5: { label: "ENTREGADOR",   color: "text-orange-300",  badgeClass: "bg-orange-500/15 border-orange-400/30 text-orange-200" },
  6: { label: "AUTORIZADO",   color: "text-yellow-300",  badgeClass: "bg-yellow-500/15 border-yellow-400/30 text-yellow-200" },
};
// Estilo neutro para tipos customizados por-cliente (Passagem, VIP, etc.) que
// não estão no mapa fixo. A descrição vem do backend em `tipo_cadastro_descricao`.
const TIPO_CADASTRO_CUSTOM = {
  color: "text-amber-300",
  badgeClass: "bg-amber-500/15 border-amber-400/30 text-amber-200",
};
const getTipoBadge = (codigo, descricaoBackend) => {
  const known = TIPO_CADASTRO_MAP[codigo];
  if (known) return known;
  // Tipo customizado — usa a descrição vinda do banco (ex.: "Passagem").
  const label = (descricaoBackend || "").toString().trim().toUpperCase() || `TIPO ${codigo ?? "?"}`;
  return { label, ...TIPO_CADASTRO_CUSTOM };
};

/* =========== CARD DO VISITANTE =========== */
function VisitanteCard({ visitante, placaBuscada, onAcaoEntradaSaida, onIncluir, onVerMovimentos, onFinalizarCiclo, onEditar }) {
  const v = visitante;
  const fotoUrl = v.foto_url || null;

  // Bloqueio herdado da pessoa_visitada (morador visitado). Prioridade:
  // motivo_bloqueio > validade_final vencida > ativo=false. Enriquecido no
  // backend em GET /cliente/veiculos/pesquisar.
  const pvBloqueio = v.pessoa_visitada_situacao || null;

  const cicloFinalizado = !!v.ciclo_finalizado;

  // qtdes: prioriza contadores agregados (multi-ciclo); fallback para 0/1 baseado nos campos legados
  const temEntrada = !!v.data_hora_entrada;
  const temSaida = !!v.data_hora_saida;
  const qtdeEntradas = v.qtde_entradas ?? (temEntrada ? 1 : 0);
  const qtdeSaidas = v.qtde_saidas ?? (temSaida ? 1 : 0);

  // Verifica se a validade está ativa.
  // Sem datas (permanencia_inicial/final) → 1 ciclo único: fecha após a 1ª saída.
  // Quando há datas, compara o DATETIME completo (não só a data) — assim "30/06 02:00 a
  // 30/06 02:36" expira corretamente às 02:37 mesmo dentro do mesmo dia.
  const validadeAtiva = !cicloFinalizado && (() => {
    const agora = new Date();
    if (!v.permanencia_inicial && !v.permanencia_final) {
      // Cadastro sem validade → permite 1 entrada e 1 saída; após isso, considera encerrado.
      return qtdeSaidas < 1;
    }
    if (v.permanencia_inicial) {
      const pi = new Date(v.permanencia_inicial);
      if (!isNaN(pi.getTime()) && agora < pi) return false;
    }
    if (v.permanencia_final) {
      const pf = new Date(v.permanencia_final);
      if (!isNaN(pf.getTime()) && agora > pf) return false;
    }
    return true;
  })();

  // Ciclo incompleto: visitante DENTRO (entrou e não saiu) ou contadores agregados indicam
  // saída pendente. Quando a validade expira nessa situação, ainda permitimos Saída/Finalizar
  // para o operador encerrar o cadastro corretamente.
  const cicloIncompletoDentro = !cicloFinalizado && (
    v.status_atual === "DENTRO" || qtdeEntradas > qtdeSaidas
  );

  // Cadastro nunca foi usado (sem entrada nem saída registradas). Quando expirado, ainda
  // permitimos Finalizar para que o operador encerre o registro sem precisar criar um novo.
  const semMovimento = !cicloFinalizado && !temEntrada && !temSaida
    && qtdeEntradas === 0 && qtdeSaidas === 0;

  let cicloEstado;
  if (cicloFinalizado) {
    cicloEstado = "expirado";
  } else if (validadeAtiva) {
    if (v.status_atual === "DENTRO") {
      cicloEstado = "saida"; // mostrar botão SAÍDA
    } else if (v.status_atual === "FORA") {
      cicloEstado = "entrada"; // mostrar botão ENTRADA (re-entrar)
    } else {
      // Fallback legado (cadastros sem status_atual)
      cicloEstado = !temEntrada ? "entrada" : (!temSaida ? "saida" : "entrada");
    }
  } else if (cicloIncompletoDentro) {
    // Validade expirou MAS visitante ainda está dentro → Saída + Finalizar
    cicloEstado = "saida_pos_validade";
  } else {
    cicloEstado = "expirado";
  }

  // Veículo: prioriza a placa que casou com a busca
  const placaLimpa = (placaBuscada || "").toUpperCase().replace(/[-\s]/g, "");
  const placaPrincipal = (v.placa || "").toUpperCase().replace(/[-\s]/g, "");
  const bateu = placaLimpa && placaPrincipal.includes(placaLimpa);
  const veiculoDisplay = {
    placa: v.placa,
    cor: v.cor_veiculo,
    descricao: v.modelo_veiculo,
    vaga: v.vaga,
  };
  void bateu; // só sinaliza intenção; sem veículos adicionais para visitante.

  // Local + EQL composto
  const linha2Parts = [];
  if (v.local_acesso_nome) linha2Parts.push(v.local_acesso_nome);
  if (v.unidade) linha2Parts.push(`Un. ${v.unidade}`);

  const linha3Parts = [];
  if (v.andar) linha3Parts.push(`And. ${v.andar}`);
  if (v.bloco) linha3Parts.push(v.bloco);
  if (v.predio) linha3Parts.push(v.predio);

  const detalhesParts = [v.empresa_nome, v.motivo].filter(Boolean);
  const tipoBadge = getTipoBadge(v.tipo_cadastro, v.tipo_cadastro_descricao);

  return (
    <div data-testid={`visitante-card-${v.id}`} className="rounded-lg bg-white/5 border border-brand-ice/10 p-3">
      <div className="flex gap-3">
        {/* Foto */}
        <div className="flex-shrink-0 w-16 h-20 rounded-lg overflow-hidden bg-brand-dark/60 border border-brand-ice/10 flex items-center justify-center">
          {fotoUrl ? (
            <img src={fotoUrl} alt={v.nome} className="w-full h-full object-cover" />
          ) : (
            <User className="w-8 h-8 text-brand-ice/20" />
          )}
        </div>

        {/* Dados */}
        <div className="flex-1 min-w-0">          {/* Linha 1: Tipo (destacado) + EQL + Monitorado */}
          <div className="flex items-center gap-2 flex-wrap">
            <span
              data-testid={`visitante-tipo-badge-${v.id}`}
              className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-heading font-extrabold tracking-wider ${tipoBadge.badgeClass}`}
            >
              {tipoBadge.label}
            </span>
            <EQLLine eql={v.unidade} />
            {(() => {
              const m = getMonitStatus(v);
              if (!m.monitorado) return null;
              return (
                <span
                  data-testid={`busca-visitante-monitorado-${v.id}`}
                  title={m.hint}
                  className={`inline-flex items-center cursor-help ${m.iconClass}`}
                >
                  <AlertTriangle className="w-4 h-4" />
                </span>
              );
            })()}
          </div>
          <p className="text-sm text-brand-ice font-medium truncate">{v.nome}</p>

          {/* Detalhes: empresa | motivo */}
          {detalhesParts.length > 0 && (
            <p className="text-xs text-brand-ice/40 truncate">{detalhesParts.join(" | ")}</p>
          )}

          {/* Pessoa Visitada + Autorizado por */}
          {(v.pessoa_visitada_nome || v.quemautorizou !== undefined) && (
            <p className="text-xs text-brand-ice/40 truncate">
              {v.pessoa_visitada_nome && (
                <>
                  <span className="text-brand-ice/30">Visitando:</span> {v.pessoa_visitada_nome}
                </>
              )}
              {v.pessoa_visitada_nome && " | "}
              <span className="text-brand-ice/30">Autorizado por:</span> {v.quemautorizou || ""}
            </p>
          )}

          {/* Validade */}
          <ValidadeLine inicial={v.permanencia_inicial} final={v.permanencia_final} />

          {/* Linha 2: Local + Unidade */}
          {linha2Parts.length > 0 && (
            <p className="text-sm text-brand-ice/50 mt-0.5 truncate">{linha2Parts.join(", ")}</p>
          )}

          {/* Linha 3: Andar, Bloco, Prédio */}
          {linha3Parts.length > 0 && (
            <p className="text-sm text-brand-ice/50 truncate">{linha3Parts.join(", ")}</p>
          )}

          {/* WhatsApp */}
          <WhatsAppLine ddi={v.ddi_celular} celular={v.celular} />

          {/* Veículo */}
          {(veiculoDisplay.placa || veiculoDisplay.cor || veiculoDisplay.descricao) && (
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {veiculoDisplay.placa && <span className="text-[15px] font-mono font-bold text-amber-400">{veiculoDisplay.placa}</span>}
              {veiculoDisplay.cor && <span className="text-sm text-brand-ice/50">{veiculoDisplay.cor}</span>}
              {veiculoDisplay.descricao && <span className="text-sm text-brand-ice/50">{veiculoDisplay.descricao}</span>}
              {veiculoDisplay.vaga && <span className="text-sm text-brand-ice/60 font-mono">Vg: {veiculoDisplay.vaga}</span>}
            </div>
          )}

          {/* Datas exclusivas: Cadastro / Entrada / Saída */}
          <div className="flex items-center gap-3 mt-1.5 flex-wrap text-[11px] text-brand-ice/50">
            {v.data_cadastro && (
              <span><span className="text-brand-ice/40">Cadastro:</span> <span className="font-mono text-brand-ice/70">{fmtDateTimeShort(v.data_cadastro)}</span></span>
            )}
            {v.data_hora_entrada && (
              <span><span className="text-brand-ice/40">Entrada:</span> <span className="font-mono text-green-400">{fmtDateTimeShort(v.data_hora_entrada)}</span></span>
            )}
            {v.data_hora_saida && (
              <span><span className="text-brand-ice/40">Saída:</span> <span className="font-mono text-green-400">{fmtDateTimeShort(v.data_hora_saida)}</span></span>
            )}
          </div>

          {/* Operador + Portaria que registrou o cadastro */}
          {(v.operador_nome || v.portaria_nome) && (
            <div className="flex items-center gap-3 mt-0.5 flex-wrap text-[11px] text-brand-ice/50">
              {v.operador_nome && (
                <span><span className="text-brand-ice/40">Operador:</span> <span className="text-brand-ice/70">{v.operador_nome}</span></span>
              )}
              {v.portaria_nome && (
                <span><span className="text-brand-ice/40">Portaria:</span> <span className="text-brand-ice/70">{v.portaria_nome}</span></span>
              )}
            </div>
          )}
        </div>

        {/* Coluna de Ações: botões com visibilidade condicional */}
        <div className="flex-shrink-0 flex flex-col items-end justify-start gap-1.5 pl-1">
          {/* Botão Editar — sempre disponível */}
          {onEditar && (
            <button
              type="button"
              data-testid={`visitante-btn-editar-${v.id}`}
              onClick={() => onEditar(v)}
              title="Editar cadastro do visitante"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-sky-500/15 hover:bg-sky-500/25 border border-sky-400/30 text-sky-300 hover:text-sky-200 transition-colors text-[11px] font-heading font-bold"
            >
              <Pencil className="w-3.5 h-3.5" />
              Editar
            </button>
          )}
          {/* Badge de Status quando há multi-ciclo (qtde_entradas + qtde_saidas > 0) */}
          {(qtdeEntradas + qtdeSaidas) > 0 && (
            <button
              type="button"
              data-testid={`visitante-btn-historico-${v.id}`}
              onClick={() => onVerMovimentos && onVerMovimentos(v)}
              title={`Histórico: ${qtdeEntradas} entrada(s) · ${qtdeSaidas} saída(s)`}
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[10px] font-heading font-bold transition-colors ${
                cicloFinalizado
                  ? "bg-slate-500/15 hover:bg-slate-500/25 border-slate-400/30 text-slate-300"
                  : v.status_atual === "DENTRO"
                  ? "bg-emerald-500/15 hover:bg-emerald-500/25 border-emerald-400/30 text-emerald-300"
                  : "bg-white/5 hover:bg-white/10 border-brand-ice/20 text-brand-ice/70"
              }`}
            >
              <History className="w-3 h-3" />
              {cicloFinalizado
                ? "Encerrado"
                : v.status_atual === "DENTRO" ? "Dentro" : "Fora"} · {qtdeEntradas}↑/{qtdeSaidas}↓
            </button>
          )}
          {cicloEstado === "entrada" && onAcaoEntradaSaida && !pvBloqueio && (
            <button
              type="button"
              data-testid={`visitante-btn-entrada-${v.id}`}
              onClick={() => onAcaoEntradaSaida(v)}
              title="Registrar Entrada (com observações)"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-green-500/15 hover:bg-green-500/25 border border-green-400/30 text-green-300 hover:text-green-200 transition-colors text-[11px] font-heading font-bold"
            >
              <LogIn className="w-3.5 h-3.5" />
              Entrada
            </button>
          )}
          {(cicloEstado === "saida" || cicloEstado === "saida_pos_validade") && onAcaoEntradaSaida && !pvBloqueio && (
            <button
              type="button"
              data-testid={`visitante-btn-saida-${v.id}`}
              onClick={() => onAcaoEntradaSaida(v)}
              title={cicloEstado === "saida_pos_validade"
                ? "Registrar Saída fora da validade — observação obrigatória"
                : "Registrar Saída (com observações)"}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-500/15 hover:bg-red-500/25 border border-red-400/30 text-red-300 hover:text-red-200 transition-colors text-[11px] font-heading font-bold"
            >
              <LogOut className="w-3.5 h-3.5" />
              Saída
            </button>
          )}
          {/* Minicard vermelho quando a pessoa_visitada está inativa/bloqueada/vencida.
              Substitui os botões Entrada/Saída/Finalizar — mesma UX do MoradorCard. */}
          {pvBloqueio && (
            <span
              data-testid={`visitante-pv-bloqueio-${v.id}`}
              title={pvBloqueio.motivo}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/25 border border-red-400/60 text-red-200 text-[11px] font-heading font-bold uppercase tracking-wider max-w-[240px] truncate"
            >
              ⛔ Visitado: {pvBloqueio.label}
            </span>
          )}
          {/* Finalizar Ciclo: disponível enquanto não estiver finalizado E existe algo a finalizar
              (validade ativa, ciclo incompleto/aberto OU cadastro nunca usado). Sem movimento
              + expirado também permite Finalizar — encerra o registro legado sem precisar criar
              um novo cadastro. Continua disponível mesmo quando o visitado está bloqueado, pois
              serve como saída de emergência. */}
          {!cicloFinalizado && (validadeAtiva || cicloIncompletoDentro || semMovimento) && onFinalizarCiclo && (
            <button
              type="button"
              data-testid={`visitante-btn-finalizar-${v.id}`}
              onClick={() => onFinalizarCiclo(v)}
              title="Encerrar o ciclo deste visitante (impede novos acessos mesmo dentro da validade)"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/15 hover:bg-amber-500/25 border border-amber-400/30 text-amber-300 hover:text-amber-200 transition-colors text-[11px] font-heading font-bold"
            >
              <Ban className="w-3.5 h-3.5" />
              Finalizar
            </button>
          )}
          {cicloEstado === "expirado" && onIncluir && (
            <button
              type="button"
              data-testid={`visitante-btn-incluir-${v.id}`}
              onClick={() => onIncluir(v)}
              title={cicloFinalizado ? "Ciclo encerrado — criar novo cadastro a partir deste" : "Validade expirada — criar novo cadastro a partir deste"}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-pink-500/15 hover:bg-pink-500/25 border border-pink-400/30 text-pink-300 hover:text-pink-200 transition-colors text-[11px] font-heading font-bold"
            >
              <Plus className="w-3.5 h-3.5" />
              Incluir
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========== CARD DA AGENDA =========== */
function AgendaCard({ agenda, placaBuscada, onConfirmar, onCriarNovaAgenda }) {
  const a = agenda;

  // Bloqueio herdado da pessoa_visitada (morador visitado). Prioridade:
  // motivo_bloqueio > validade_final vencida > ativo=false.
  const pvBloqueio = a.pessoa_visitada_situacao || null;

  // Detecta se a validade da agenda já expirou. A API não devolve um campo
  // `validade_status`; calculamos localmente a partir de `permanencia_final`
  // — mesma abordagem do componente ValidadeLine.
  const agendaVencida = (() => {
    if (!a?.permanencia_final) return false;
    try {
      const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
      const [y, m, d] = String(a.permanencia_final).split("T")[0].split("-");
      const fim = new Date(Number(y), Number(m) - 1, Number(d));
      fim.setHours(0, 0, 0, 0);
      return fim < hoje;
    } catch {
      return false;
    }
  })();

  // Veículo (se a agenda registrar)
  const placaLimpa = (placaBuscada || "").toUpperCase().replace(/[-\s]/g, "");
  const placaPrincipal = (a.placa || "").toUpperCase().replace(/[-\s]/g, "");
  const bateu = placaLimpa && placaPrincipal.includes(placaLimpa);
  void bateu;

  const linha2Parts = [];
  if (a.local_acesso_nome) linha2Parts.push(a.local_acesso_nome);
  if (a.unidade) linha2Parts.push(`Un. ${a.unidade}`);

  const linha3Parts = [];
  if (a.andar) linha3Parts.push(`And. ${a.andar}`);
  if (a.bloco) linha3Parts.push(a.bloco);
  if (a.predio) linha3Parts.push(a.predio);

  const detalhesParts = [a.motivo].filter(Boolean);
  const tipoBadge = getTipoBadge(a.tipo_cadastro, a.tipo_cadastro_descricao);
  const visitandoOuAutorizador = a.pessoa_visitada_nome
    ? { label: "Visitando", nome: a.pessoa_visitada_nome }
    : (a.autorizador_nome ? { label: "Autorizado", nome: a.autorizador_nome } : null);

  // Status com cor
  const statusMap = {
    pendente: { label: "Pendente", cls: "bg-amber-500/20 text-amber-300 border-amber-400/30" },
    confirmada: { label: "Confirmada", cls: "bg-green-500/20 text-green-300 border-green-400/30" },
    cancelada: { label: "Cancelada", cls: "bg-red-500/20 text-red-300 border-red-400/30" },
    realizada: { label: "Realizada", cls: "bg-sky-500/20 text-sky-300 border-sky-400/30" },
  };
  const st = statusMap[(a.status || "").toLowerCase()] || null;

  return (
    <div data-testid={`agenda-card-${a.id}`} className="rounded-lg bg-white/5 border border-brand-ice/10 p-3">
      <div className="flex gap-3">
        {/* Ícone */}
        <div className="flex-shrink-0 w-16 h-20 rounded-lg overflow-hidden bg-brand-dark/60 border border-brand-ice/10 flex items-center justify-center">
          <Calendar className="w-8 h-8 text-green-400/40" />
        </div>

        {/* Dados */}
        <div className="flex-1 min-w-0">
          {/* Linha 1: Tipo (destacado) + status badge + EQL */}
          <div className="flex items-center gap-2 flex-wrap">
            <span
              data-testid={`agenda-tipo-badge-${a.id}`}
              className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-heading font-extrabold tracking-wider ${tipoBadge.badgeClass}`}
            >
              {tipoBadge.label}
            </span>
            {st && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${st.cls}`}>{st.label}</span>
            )}
            <EQLLine eql={a.unidade} />
          </div>
          <p className="text-sm text-brand-ice font-medium truncate">{a.nome || a.nome_visitante}</p>

          {/* Detalhes */}
          {detalhesParts.length > 0 && (
            <p className="text-xs text-brand-ice/40 truncate">{detalhesParts.join(" | ")}</p>
          )}

          {/* Pessoa Visitada / Autorizador (linha própria pra evitar overflow horizontal) */}
          {visitandoOuAutorizador && (
            <p className="text-xs text-brand-ice/40 truncate">
              <span className="text-brand-ice/30">{visitandoOuAutorizador.label}:</span> {visitandoOuAutorizador.nome}
            </p>
          )}

          {/* Validade (período de permanência) — v3.8.13: considerarHorario para
              refletir vencimento por horário em agendas do dia corrente. */}
          <ValidadeLine
            inicial={a.permanencia_inicial}
            final={a.permanencia_final}
            considerarHorario
          />

          {/* Linha 2: Local + Unidade */}
          {linha2Parts.length > 0 && (
            <p className="text-sm text-brand-ice/50 mt-0.5 truncate">{linha2Parts.join(", ")}</p>
          )}

          {/* Linha 3: Andar, Bloco, Prédio */}
          {linha3Parts.length > 0 && (
            <p className="text-sm text-brand-ice/50 truncate">{linha3Parts.join(", ")}</p>
          )}

          {/* WhatsApp */}
          <WhatsAppLine ddi={a.ddi_celular} celular={a.celular} />

          {/* Veículo (caso a agenda tenha placa) */}
          {(a.placa || a.cor_veiculo || a.modelo_veiculo) && (
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {a.placa && <span className="text-[15px] font-mono font-bold text-amber-400">{a.placa}</span>}
              {a.cor_veiculo && <span className="text-sm text-brand-ice/50">{a.cor_veiculo}</span>}
              {a.modelo_veiculo && <span className="text-sm text-brand-ice/50">{a.modelo_veiculo}</span>}
            </div>
          )}

          {/* Linha de timestamps disponíveis para agenda */}
          <div className="flex items-center gap-3 mt-1.5 flex-wrap text-[11px] text-brand-ice/50">
            {a.created_at && (
              <span><span className="text-brand-ice/40">Cadastro:</span> <span className="font-mono text-brand-ice/70">{fmtDateTimeShort(a.created_at)}</span></span>
            )}
            {a.permanencia_inicial && (
              <span><span className="text-brand-ice/40">Início:</span> <span className="font-mono text-green-400">{fmtDateTimeShort(a.permanencia_inicial)}</span></span>
            )}
            {a.permanencia_final && (
              <span><span className="text-brand-ice/40">Fim:</span> <span className="font-mono text-red-400">{fmtDateTimeShort(a.permanencia_final)}</span></span>
            )}
          </div>
        </div>

        {/* Botão de ação:
            • Se pessoa_visitada está inativa/bloqueada/vencida → minicard vermelho (bloqueia Confirmar).
            • Agenda vencida (validade_status="vencido") → "Nova agenda" (pré-preenche CPF).
              Não podemos confirmar agenda vencida como visitante.
            • Agenda pendente com CPF válido → "Confirmar" (verde).
            • Agenda pendente sem CPF → "Confirme CPF" (âmbar). */}
        {pvBloqueio ? (
          <span
            data-testid={`agenda-pv-bloqueio-${a.id}`}
            title={pvBloqueio.motivo}
            className="flex-shrink-0 self-start inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/25 border border-red-400/60 text-red-200 text-[11px] font-heading font-bold uppercase tracking-wider max-w-[240px] truncate"
          >
            ⛔ Visitado: {pvBloqueio.label}
          </span>
        ) : (agendaVencida) ? (
          onCriarNovaAgenda && (
            <button
              type="button"
              data-testid={`busca-agenda-nova-${a.id}`}
              onClick={() => onCriarNovaAgenda(a)}
              title="Agenda vencida — cria uma nova agenda usando o CPF do visitante"
              className="flex-shrink-0 self-start flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sky-500/20 hover:bg-sky-500/30 text-sky-200 border border-sky-500/40 text-xs font-heading font-medium transition"
            >
              <Plus className="w-4 h-4" />
              Nova agenda
            </button>
          )
        ) : onConfirmar && (a.status || "pendente").toLowerCase() === "pendente" && (
          (((a.cpf || "").replace(/\D/g, "")).length === 11) ? (
            <button
              type="button"
              data-testid={`busca-agenda-confirmar-${a.id}`}
              onClick={() => onConfirmar(a)}
              title="Confirmar agenda como visitante"
              className="flex-shrink-0 self-start flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-green-500/20 hover:bg-green-500/30 text-green-300 border border-green-500/40 text-xs font-heading font-medium transition"
            >
              <Check className="w-4 h-4" />
              Confirmar
            </button>
          ) : (
            <button
              type="button"
              data-testid={`busca-agenda-confirme-cpf-${a.id}`}
              onClick={() => onConfirmar(a)}
              title="CPF ausente — clique para informar"
              className="flex-shrink-0 self-start flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-brand-dark border border-amber-500 text-xs font-heading font-bold transition"
            >
              <User className="w-4 h-4" />
              Confirme CPF
            </button>
          )
        )}
      </div>
    </div>
  );
}
